/**
 * WCS bounds — per-kind LOCAL (OCS-local, pre-extrusion-transform) critical-point builders
 * (T1 arm (c)).
 *
 * Split out of `./wcsBounds.ts` so BOTH the top-level dispatch (`wcsBounds.ts`) AND the
 * block-DEFINITION-child dispatch (`./wcsBoundsBlock.ts`, which resolves a Block INSERT's
 * referenced definition recursively) reuse the SAME per-kind point math without an import
 * cycle (`wcsBounds.ts` needs `wcsBoundsBlock.ts` for its own `Block` case; `wcsBoundsBlock.ts`
 * needs these per-kind builders for the 13 shared kinds a block definition may contain —
 * mirrors the `cad-core/bounds.ts` ↔ `blockGeometry.ts` ↔ `leafBounds.ts` three-way split for
 * the identical reason, see `blockGeometry.ts`'s own module doc).
 *
 * Every builder returns OCS-LOCAL points (the entity's own drawing-plane / raw coordinates,
 * BEFORE that entity's own extrusion is applied) + the entity's `extrusion` field verbatim —
 * the caller decides how to finalize: `aabb3FromLocalPoints` for a top-level entity,
 * `ocsToWcs`-per-point (then further transformed by the enclosing INSERT chain) for a
 * block-definition child. See `./wcsBounds.ts`'s module header for the full design rationale,
 * reference sources, and the documented residual approximations (text-width heuristic, spline
 * convex-hull, tilted-OCS tessellation, ArrayRect parametric-grid scope).
 */

import type {
  ArcEntity,
  ArrayRectEntity,
  CircleEntity,
  DimensionEntity,
  DimensionStyle,
  EllipseEntity,
  HatchEntity,
  LineEntity,
  LwpolylineEntity,
  MleaderLeaderNode,
  MtextEntity,
  MultileaderEntity,
  OleFrameEntity,
  PointEntity,
  PolylineEntity,
  PolylineVertex,
  RevisionCloudEntity,
  SplineEntity,
  TextEntity,
  ViewportEntity,
  WipeoutEntity,
} from "./model";
import { dxfVertexSubtype, polylineIsClosed, polylineSubtype } from "./entityPolylineTableOle";
import { surfaceFitVertexIndices } from "./polylineSurfaceFit";
import { bulgeArc } from "./bulgeArc";
import {
  arcExtentPoints2D,
  computeOcsAxes,
  ellipseExtentPoints2D,
  ellipseExtentPoints3D,
  isDefaultExtrusion,
  isReflectedOcs,
  positiveOr,
  rotateDeg2D,
  tessellateArc2D,
  tessellateEllipse2D,
  tessellateEllipse3D,
  type Local2,
  type Wcs3,
} from "./wcsBoundsMath";
import { mtextAnchoredBoxCorners, mtextBoxSize, mtextEffectiveAngleDeg, textJustifiedLocalBox } from "./wcsBoundsText";
import { fitPointsToControlPoints } from "./splineMath";
import { cross, normalize } from "./vec3";
import { applyDimRounding, formatDimValueByUnit } from "./dimensionMeasurementFormat";

/**
 * The result every leaf builder returns: OCS-local critical points + the entity's own extrusion.
 * `extrusion` is typed `| undefined` (not just optional) so a builder can EXPLICITLY assert "no
 * OCS transform applies" (e.g. a WCS-only kind like Line/Point, or a kind whose own extrusion is
 * a mirror-handedness marker rather than a plane tilt, like MText) under `exactOptionalPropertyTypes`
 * — @codex T1 arm(c) round-8 P1 finding: distinct from simply OMITTING the field, which would read
 * as "no opinion" rather than a deliberate, documented "identity" assertion at each call site.
 */
export interface LocalPointSet {
  readonly points: readonly Wcs3[];
  readonly extrusion?: { x: number; y: number; z: number } | undefined;
}

const Z = (p: { z?: number | undefined }): number => p.z ?? 0;

/** `[p, p + thickness·ẑ]` when `thickness` is non-zero (OCS-local — the shared thickness-extrude step). */
export function withThicknessLocal(points: readonly Wcs3[], thickness: number): Wcs3[] {
  if (Math.abs(thickness) < 1e-9) return [...points];
  return [...points, ...points.map((p) => ({ x: p.x, y: p.y, z: p.z + thickness }))];
}

/**
 * `[p, p + thickness·extrusionUnitVector]` when `thickness` is non-zero — the WCS-frame
 * equivalent of {@link withThicknessLocal} for a kind whose OWN coordinates are ALREADY
 * WCS-absolute (never fed through `ocsToWcs`), so the extrude direction has to be baked in
 * HERE instead of arriving "for free" via a later OCS transform of a local `ẑ` offset.
 *
 * Autodesk DXF reference ("Object Coordinate Systems (OCS)"): *"The following entities do
 * not lie in a particular plane. All points are expressed in world coordinates... Of these
 * entities, only lines and points can be extruded. **Their extrusion direction can differ
 * from the world z-axis.**"* — LINE/POINT are the one DOCUMENTED case of a non-OCS-planar
 * entity whose `thickness` STILL extrudes along a tilted `extrusion` vector (unlike every
 * other WCS-only field on those two kinds, which stays untouched by `extrusion`).
 *
 * @codex T1 arm(c) round-23 P2 finding (F1): `ellipseLocal` (round-22) is a SECOND user of this
 * helper, for a related but distinct reason — its OWN points are real WCS-final coordinates by
 * this module's own architectural choice (SL-15, `extrusion: undefined` returned so no further
 * `ocsToWcs` is ever applied), not because Ellipse is on Autodesk's documented non-planar-entity
 * list. Whatever the reason, the SHAPE of the problem is identical: a WCS-final point set whose
 * `thickness` must still extrude along the entity's real 3D extrusion vector, not an invented
 * WCS-Z — so `ellipseLocal` reuses this exact helper rather than a third near-duplicate.
 */
export function withThicknessAlongExtrusion(
  points: readonly Wcs3[],
  thickness: number,
  extrusion: { x: number; y: number; z: number } | undefined,
): Wcs3[] {
  if (Math.abs(thickness) < 1e-9) return [...points];
  const az = computeOcsAxes(extrusion).az; // the normalized extrusion vector (identity ẑ when default/absent).
  return [...points, ...points.map((p) => ({ x: p.x + thickness * az.x, y: p.y + thickness * az.y, z: p.z + thickness * az.z }))];
}

/**
 * One straight/bulged polyline SEGMENT's local critical points (LWPOLYLINE / Revision Cloud
 * share this). `dense` additionally tessellates a BULGED segment's arc (see `polylineLocalPoints`
 * / this module's `dense`-parameter doc on `circleLocal` for why).
 *
 * THE SEGMENT'S OWN AUTHORED ENDPOINTS ARE ALWAYS EMITTED VERBATIM — for the bulged branch too,
 * not just the straight one. A bulged segment is the one arc in this module whose endpoints are
 * the AUTHORED data and whose centre/radius/angles are DERIVED from them (`bulgeArc`: group 42
 * `bulge = tan(θ/4)` + the two vertices ⇒ centre/radius/sweep), the exact inverse of ARC/HATCH-
 * arc-edge, where centre/radius/angles are authored and the endpoints have no independent
 * existence. `arcExtentPoints2D` can only RE-DERIVE an endpoint as `polarPoint2D(cx, cy, r,
 * angle)`, and that round trip costs `≈ eps·|r|` of absolute error — bounded and irrelevant when
 * `|r|` is the same order as the segment, but UNBOUNDED relative to the segment as `bulge → 0`,
 * because `|r| = chord / (2·|sin(2·atan(bulge))|) → ∞` while the segment stays put. Feeding ONLY
 * the re-derived points to the AABB therefore produced a box that EXCLUDED the polyline's own
 * first vertex: the measured witness `v1=(0,0) bulge=-1.9799912757598696e-11 v2=(-100000,0)`
 * resolves to `r = 1.2626318260117405e15`, `eps·|r| = 0.2804`, and `polarPoint2D` reconstructs
 * `v1` as `(-0.13560150963894557, 0)` — which became the reported `maxX`, putting the authored
 * `(0,0)` 0.1356 OUTSIDE its own entity's box (pinned by `wcsBoundsLeaf.bulgeEndpoints.test.ts`).
 * Every consumer of that box is wrong in the same breath: the spatial index, window/crossing
 * selection, viewport culling, ZOOM Object and the agent-facing `entityBounds` facet.
 *
 * The authored endpoints are EXACT and lie on the arc by construction, so emitting them can only
 * tighten correctness — never loosen it. The re-derived pair from `arcExtentPoints2D` is kept
 * alongside them rather than suppressed: it is at most `eps·|r|` away from the truth and a union
 * of points only ever WIDENS the box, which is this module's standing "provably conservative,
 * never under-report" convention (see `tessellateArc2D`'s doc). This mirrors `bulgeArc.ts
 * ::bulgeSegmentBounds` — `drawing-model`'s OTHER bulge-segment box, consumed by
 * `viewportMembership.ts` and `cad-core/leafBounds.ts::bulgePolylineBounds` — which has always
 * SEEDED its box from `min/max(v1, v2)` and only then merged the crossed cardinal extrema. That
 * function cannot be reused here (it returns a 2D AABB, and an AABB cannot be re-oriented through
 * this module's later OCS / INSERT rotation — only a POINT SET can), but its rule is the one this
 * function now also follows.
 */
function segmentLocalPoints(v1: PolylineVertex, v2: PolylineVertex, elevation: number, dense: boolean): Wcs3[] {
  // The authored endpoints, carried EXACTLY — see this function's doc for why they are hoisted
  // out of the straight-segment branch instead of being left to arc-angle re-derivation.
  const endpoints: Wcs3[] = [
    { x: v1.x, y: v1.y, z: elevation },
    { x: v2.x, y: v2.y, z: elevation },
  ];
  const arc = bulgeArc(v1, v2, v1.bulge);
  if (!arc) return endpoints;
  // A CW sweep (signedSweep<0) covers the identical angular RANGE as a CCW sweep from
  // (start+signedSweep) to start — bounds only need the SPAN, not the traversal direction
  // (`wcsBoundsMath.ts::sweepDeg`'s CCW-only convention).
  const startDeg = (arc.startAngle * 180) / Math.PI;
  const sweepDegSigned = (arc.signedSweep * 180) / Math.PI;
  // Stryker disable next-line EqualityOperator: `>=` vs `>` is unobservable here on two independent
  // grounds. (1) `sweepDegSigned === 0` is UNREACHABLE past the `if (!arc)` guard above —
  // `bulgeArc` returns null unless `|bulge| >= 1e-12` (its own `EPS`), and it then sets
  // `signedSweep = ±|4·atan(bulge)|`, whose magnitude is `>= 4e-12`, never 0. (2) Even at an exact
  // 0 both arms evaluate to the SAME pair, since `startDeg + 0 === startDeg` for every finite
  // `startDeg` (and the `-0`/`+0` pair the sole exception produces feeds `polarPoint2D`, where
  // `cos(-0) === cos(0)` and `sin(±0) === ±0` — indistinguishable to a min/max fold).
  const [a0, a1] = sweepDegSigned >= 0 ? [startDeg, startDeg + sweepDegSigned] : [startDeg + sweepDegSigned, startDeg];
  // The sweep MAGNITUDE is passed explicitly rather than left to `sweepDeg` to re-infer from the
  // angle pair above — `bulgeArc` already resolved it EXACTLY, and re-inferring it is lossy in a
  // way that matters here: `sweepDeg`'s DXF full-turn convention (a `|endDeg − startDeg| < 1e-9`
  // delta MEANS a full circle, correct for the ARC entity and HATCH arc edges, whose angle pair IS
  // the authored data) cannot tell a real NEAR-ZERO sweep apart from a deliberate 360°. A bulge in
  // `[1e-12, ~4.4e-12)` — just past `bulgeArc`'s own `|bulge| < 1e-12` straight-segment guard — on
  // a large chord lands exactly in that band: `bulge=1e-12` over a 100 000-unit chord gives
  // `r = 2.5e16` and a sweep of `2.3e-10°`, which was read as a FULL TURN, so a near-straight
  // segment reported all four cardinal points of a radius-2.5e16 circle — a box 5e16 across for a
  // 1e5-long segment. `4·atan(bulge) < 2π` for every finite bulge, so a bulge-derived arc is never
  // a full turn and must never be re-read as one (see `arcExtentPoints2D`'s `knownSweepDeg` doc).
  const sweepMagDeg = Math.abs(sweepDegSigned);
  let local2 = arcExtentPoints2D(arc.cx, arc.cy, arc.radius, a0, a1, sweepMagDeg);
  if (dense) local2 = [...local2, ...tessellateArc2D(arc.cx, arc.cy, arc.radius, a0, a1, /* steps */ undefined, sweepMagDeg)];
  return [...endpoints, ...local2.map((p) => ({ x: p.x, y: p.y, z: elevation }))];
}

/**
 * Every polyline vertex's local critical points, closing-segment-aware (LWPOLYLINE / Revision
 * Cloud). `dense`: see `circleLocal`'s doc — a bulged segment's EXACT local-frame cardinal
 * extrema are not guaranteed to stay extremal once a FURTHER external rotation is applied (this
 * entity's own OCS tilt, or — for a block-definition child — the enclosing INSERT's `rotation`);
 * `dense` additionally tessellates every bulged arc so the reported box stays conservative under
 * that further rotation (@codex T1 arm(c) P1 finding — originally reported for Circle/Arc/
 * Ellipse; the identical compounding-rotation gap applies to a bulged polyline segment).
 */
export function polylineLocalPoints(vertices: readonly PolylineVertex[], closed: boolean, elevation: number, dense = false): Wcs3[] {
  if (vertices.length === 0) return [];
  const pts: Wcs3[] = [];
  for (let i = 1; i < vertices.length; i++) pts.push(...segmentLocalPoints(vertices[i - 1]!, vertices[i]!, elevation, dense));
  if (closed && vertices.length >= 2) {
    pts.push(...segmentLocalPoints(vertices[vertices.length - 1]!, vertices[0]!, elevation, dense));
  }
  if (pts.length === 0) {
    // A single-vertex / fully-degenerate polyline never reached a segment — the vertices themselves.
    for (const v of vertices) pts.push({ x: v.x, y: v.y, z: elevation });
  }
  return pts;
}

/** DXF HATCH boundary edges carry arc/ellipticArc angles in DEGREES (unlike the ELLIPSE entity's own radian params). */
function ellipseExtentPointsDeg2D(cx: number, cy: number, majorX: number, majorY: number, ratio: number, startDeg: number, endDeg: number) {
  return ellipseExtentPoints2D(cx, cy, majorX, majorY, ratio, (startDeg * Math.PI) / 180, (endDeg * Math.PI) / 180);
}

/** Degrees-angle wrapper of {@link tessellateEllipse2D} — for a HATCH `ellipticArc` boundary edge. */
function tessellateEllipseDeg2D(cx: number, cy: number, majorX: number, majorY: number, ratio: number, startDeg: number, endDeg: number) {
  return tessellateEllipse2D(cx, cy, majorX, majorY, ratio, (startDeg * Math.PI) / 180, (endDeg * Math.PI) / 180);
}

/**
 * LINE is NOT an OCS-planar entity (Autodesk DXF reference, "Object Coordinate Systems
 * (OCS)": *"The following entities do not lie in a particular plane. All points are
 * expressed in world coordinates"* — the LINE group-code table itself labels 10/20/30 and
 * 11/21/31 "(in WCS)"). `start`/`end` are therefore returned WCS-final here — `extrusion:
 * undefined` (never fed through `ocsToWcs`, unlike every OCS-planar kind in this module) —
 * @codex T1 arm(c) round-7 P2 finding: the prior code returned `e.extrusion` verbatim, so
 * `applyOwnOcs`/`aabb3FromLocalPoints` rotated an ALREADY-WCS start/end through the Arbitrary
 * Axis Algorithm, silently relocating an imported LINE with a genuine (non-default) extrusion.
 * `thickness` still extrudes along that (possibly tilted) `extrusion` vector — the one
 * documented case where a non-OCS-planar entity's extrusion remains geometrically live (see
 * {@link withThicknessAlongExtrusion}'s doc).
 */
export function lineLocal(e: LineEntity): LocalPointSet {
  const base: Wcs3[] = [
    { x: e.start.x, y: e.start.y, z: Z(e.start) },
    { x: e.end.x, y: e.end.y, z: Z(e.end) },
  ];
  return { points: withThicknessAlongExtrusion(base, e.thickness, e.extrusion), extrusion: undefined };
}

/** POINT is WCS-final too (identical reasoning to {@link lineLocal} — same DXF OCS-entity exclusion list). */
export function pointLocal(e: PointEntity): LocalPointSet {
  const base: Wcs3[] = [{ x: e.location.x, y: e.location.y, z: Z(e.location) }];
  return { points: withThicknessAlongExtrusion(base, e.thickness, e.extrusion), extrusion: undefined };
}

/**
 * `dense` (default `false`, @codex T1 arm(c) P1 finding): the exact 4 local-frame cardinal
 * points bound a circle ONLY in the frame they were computed in. A circle is rotationally
 * symmetric, so its TRUE bounds are invariant under rotation — but 4 DISCRETE sampled points
 * are NOT (rotating 4 samples by 45° no longer touches the circle's new cardinal extrema,
 * under-reporting the box). Top-level dispatch (`wcsBounds.ts`) passes `dense=false` and relies
 * on the OWN-extrusion `isDefaultExtrusion` gate below (this entity's own OCS tilt is the only
 * further rotation a top-level entity undergoes). Block-DEFINITION-child dispatch
 * (`wcsBoundsBlock.ts`) ALWAYS passes `dense=true` — a block child undergoes a FURTHER,
 * a-priori-unknown rotation from the enclosing INSERT's own `rotation` (and any ancestor
 * INSERT's rotation, for nested blocks), so the child's own extrusion state alone cannot gate
 * tessellation; the compounding rotation must always be treated as "may rotate this circle".
 */
export function circleLocal(e: CircleEntity, dense = false): LocalPointSet {
  const cz = Z(e.center);
  let base: Wcs3[] = [
    { x: e.center.x - e.radius, y: e.center.y, z: cz },
    { x: e.center.x + e.radius, y: e.center.y, z: cz },
    { x: e.center.x, y: e.center.y - e.radius, z: cz },
    { x: e.center.x, y: e.center.y + e.radius, z: cz },
  ];
  if (dense || !isDefaultExtrusion(e.extrusion)) {
    const tess = tessellateArc2D(e.center.x, e.center.y, e.radius, 0, 360);
    base = [...base, ...tess.map((p) => ({ x: p.x, y: p.y, z: cz }))];
  }
  return { points: withThicknessLocal(base, e.thickness), extrusion: e.extrusion };
}

/** `dense` — see {@link circleLocal}'s doc (identical compounding-rotation reasoning for Arc). */
export function arcLocal(e: ArcEntity, dense = false): LocalPointSet {
  const cz = Z(e.center);
  let local2 = arcExtentPoints2D(e.center.x, e.center.y, e.radius, e.startAngle, e.endAngle);
  if (dense || !isDefaultExtrusion(e.extrusion)) {
    local2 = [...local2, ...tessellateArc2D(e.center.x, e.center.y, e.radius, e.startAngle, e.endAngle)];
  }
  const base: Wcs3[] = local2.map((p) => ({ x: p.x, y: p.y, z: cz }));
  return { points: withThicknessLocal(base, e.thickness), extrusion: e.extrusion };
}

/**
 * The ellipse's REAL 3D minor-axis vector — magnitude `|majorAxis3D| · ratio`, direction
 * `normalize(extrusion × majorAxis3D)` — the Autodesk/ezdxf ELLIPSE definition AND the render
 * tessellator's OWN formula (`apps/web/src/app/scene3d/adapters/arcEllipseSpline.ts
 * ::ellipseMinorUnit`/`tessellateEllipse`'s doc: `"minor = extrusion × major_axis"`).
 * `packages/drawing-model` cannot import `apps/web` (wrong dependency direction,
 * `.dependency-cruiser.cjs` forbids it), so this is re-derived here rather than shared — same
 * `wcsBoundsMath.ts::guardInsertScale` precedent this module family already follows.
 *
 * @codex T1 arm(c) round-21 P2 finding (F4): for the DEFAULT `(0,0,1)` extrusion this is ALWAYS a
 * pure-XY vector — `cross((0,0,1), majorAxis3D).z === 0·majorAxis3D.y − 0·majorAxis3D.x === 0` for
 * ANY `majorAxis3D`, regardless of its own `.z` — so it never widens `ellipseLocal`'s Z range for
 * the overwhelmingly common planar case (byte-identical to pre-fix behavior there). When
 * `extrusion` genuinely tilts the plane, the minor axis CAN carry a real Z component that the
 * purely-XY `ellipseExtentPoints2D`/`tessellateEllipse2D` computation never accounts for (that
 * computation implicitly assumes the minor axis is a 90°-in-plane rotation of the major axis —
 * only true for the default extrusion).
 *
 * @codex T1 arm(c) round-23 P2 finding: the degenerate case (major axis ∥ extrusion — a
 * schema-valid but malformed ellipse whose major axis lies along its own plane normal) previously
 * returned the zero vector, on the theory that "zero additional data" was the honest answer for a
 * bounds-only consumer. That reasoning does not survive contact with the RENDERER: the render's
 * OWN one-truth minor-axis direction (`apps/web/…/scene3d/adapters/arcEllipseSpline.ts
 * ::ellipseMinorUnit`) does NOT leave the minor axis at zero for this input — it picks an
 * arbitrary-but-deterministic perpendicular direction (cross the normalized major axis with world
 * Z, or with world X when the major axis is itself near-parallel to world Z) and still draws a
 * full-width ellipse. Leaving the minor axis at zero here silently collapsed `entityBounds` to the
 * major-axis LINE for exactly the malformed input the renderer still draws as a real ellipse — the
 * fix mirrors `ellipseMinorUnit`'s fallback EXACTLY (same `|ax.z| < 0.9` reference-axis tie-break),
 * scaled by this function's own `majorMag · ratio` minor-axis magnitude.
 *
 * @codex T1 arm(c) round-23 P2 finding follow-up (property-test-caught, F5) — SUPERSEDED by round-24
 * (see the finding directly below): this paragraph is kept only as a historical record of the
 * reasoning round-24 reverts. Round-23 made the degeneracy check SCALE-INVARIANT (relative to
 * `|normal|·|majorAxis3D|`), not a fixed absolute epsilon, on the theory that `raw = cross(normal,
 * majorAxis3D)` crosses two UNNORMALIZED vectors, so its floating-point noise floor for a
 * mathematically-EXACT-parallel pair scales with the PRODUCT of their magnitudes (`≈
 * |normal|·|majorAxis3D|·Number.EPSILON`) — a large-magnitude exactly-parallel pair could otherwise
 * produce a `rawLen` comfortably above a fixed absolute threshold purely from rounding noise,
 * `normalize(raw)` then returning an essentially RANDOM unit direction scaled by a real, non-
 * negligible `minorMag`. That reasoning is INTERNALLY sound but was the wrong fix: see below.
 *
 * @codex T1 arm(c) round-24 P2 finding: round-23's scale-relative threshold made this function's
 * degenerate/non-degenerate BRANCH SELECTION diverge from the RENDERER's — and the renderer is the
 * one true target this whole module bounds (this file's own header: "mirror the renderer's exact
 * formula, even when that formula has its own numerical quirks, because the goal is bounding what's
 * ACTUALLY DRAWN, not an idealized abstraction of it"). The renderer's real `ellipseMinorUnit`
 * (`apps/web/…/scene3d/adapters/arcEllipseSpline.ts`) uses a FIXED ABSOLUTE threshold on the raw,
 * unnormalized cross-product magnitude — `if (mlen > 1e-12) { return normalized; }` — no
 * magnitude-relative scaling at all. For CAD-typical vectors (magnitude ~10–1000, common insertion/
 * major-axis scales), round-23's `Math.max(normalMag·majorMag,1)·1e-9` threshold evaluates to
 * roughly `1e-9`…`1e-3` — potentially FAR larger than a genuinely near-but-not-quite-parallel pair's
 * REAL (small but non-noise) cross-product magnitude, wrongly routing that case to the arbitrary tie-
 * break fallback instead of the small-but-CORRECT direction the renderer's own `1e-12` threshold
 * actually computes and draws — putting this function's minor axis on the WRONG SIDE (180° off, or
 * simply a different perpendicular entirely) of where the renderer puts it, under-reporting a
 * partial ellipse's true arc extent. The fix is to match the RENDERER's exact numeric behavior, not
 * what seems more "robust" in isolation: revert to the renderer's EXACT absolute `1e-12` threshold
 * on `rawLen`. This means round-23's own "large-magnitude exactly-parallel noise" concern (see the
 * superseded paragraph above) is a genuine RESIDUAL risk for EXTREME-magnitude inputs — but since
 * the renderer ITSELF has the exact same absolute-threshold behavior (and thus the exact same
 * theoretical noise-sensitivity), reproducing it exactly is CORRECT: whatever direction the
 * renderer's noisy `1e-12`-threshold cross product computes for an extreme-magnitude case, THIS
 * function must compute the SAME thing, not a "more correct" but DIFFERENT thing. The
 * `majorMag < 1e-9` guard above is UNCHANGED — that one guards the separate DEGENERATE-MAJOR-AXIS
 * case (a near-zero major axis has no real direction to speak of at all), not this branch's
 * threshold.
 */
function ellipseMinorAxis3D(majorAxis3D: Wcs3, extrusion: { x: number; y: number; z: number } | undefined, ratio: number): Wcs3 {
  const majorMag = Math.hypot(majorAxis3D.x, majorAxis3D.y, majorAxis3D.z);
  if (majorMag < 1e-9) return { x: 0, y: 0, z: 0 };
  const normal = extrusion ?? { x: 0, y: 0, z: 1 };
  const minorMag = majorMag * ratio;
  const raw = cross(normal, majorAxis3D);
  const rawLen = Math.hypot(raw.x, raw.y, raw.z);
  // @codex T1 arm(c) round-24 P2 finding: the renderer's OWN exact absolute threshold
  // (`ellipseMinorUnit`'s `mlen > 1e-12`) — see this function's doc above for why matching it
  // EXACTLY (not a scale-relative "improvement") is the correct fix.
  if (rawLen > 1e-12) {
    const dir = normalize(raw);
    return { x: dir.x * minorMag, y: dir.y * minorMag, z: dir.z * minorMag };
  }
  // Degenerate: majorAxis3D ∥ extrusion — mirror `ellipseMinorUnit`'s own arbitrary-perpendicular
  // tie-break EXACTLY (see this function's own doc above): normalize the major axis, pick world Z
  // as the reference unless the major axis is itself near-parallel to world Z (then use world X),
  // and cross the two — always non-degenerate by construction (the reference is never parallel to
  // the axis it is crossed with, given the `<0.9` threshold selection).
  const ax = { x: majorAxis3D.x / majorMag, y: majorAxis3D.y / majorMag, z: majorAxis3D.z / majorMag };
  const ref: Wcs3 = Math.abs(ax.z) < 0.9 ? { x: 0, y: 0, z: 1 } : { x: 1, y: 0, z: 0 };
  const dir = normalize(cross(ax, ref));
  return { x: dir.x * minorMag, y: dir.y * minorMag, z: dir.z * minorMag };
}

/**
 * `dense` — see {@link circleLocal}'s doc (identical compounding-rotation reasoning for Ellipse'
 * OWN local XY parametric extrema — the tessellation gate no longer references `e.extrusion`,
 * see the `extrusion: undefined` note below).
 *
 * `extrusion` is deliberately OMITTED from the returned `LocalPointSet` (@codex T1 arm(c) round-2
 * P2 finding): `model.ts`'s `ellipseEntitySchema` doc (SL-15) documents `center`/
 * `majorAxisEndpoint` as FULL 3D **WCS** coordinates (not OCS-local) — "the bridge exports
 * `z ?? 0` (writer always emits 30/31, matching ezdxf `ellipse.py` `center`/`major_axis = Vec3`)".
 * `extrusion` (group 210/220/230) is a round-trip-only plane-orientation marker for THIS model's
 * chosen representation, NOT a WHOLE-POINT-SET transform a bounds consumer should re-apply via
 * `ocsToWcs`/the Arbitrary Axis Algorithm — doing so WOULD double-count the tilt (once via
 * `majorAxisEndpoint.z`, once via the Arbitrary Axis Algorithm), rotating an imported tilted/
 * elevated ellipse's bounds around the WCS origin into the wrong place. That original round-2
 * concern is still correct and is why `extrusion: undefined` is still returned below.
 *
 * @codex T1 arm(c) round-21 P2 finding (F4) — narrower TRUE gap the round-2 doc did NOT cover:
 * `extrusion` is additionally used, WITHOUT double-counting, in a SECOND, narrower way — as the
 * plane normal that fixes the ellipse's REAL MINOR-AXIS DIRECTION (`extrusion × majorAxis`, see
 * `ellipseMinorAxis3D`'s doc), exactly like the render tessellator's own
 * `arcEllipseSpline.ts::tessellateEllipse`/`ellipseMinorUnit`. A tilted extrusion with
 * `majorAxisEndpoint.z === 0` (planar major axis, tilted plane normal) previously left the Z range
 * pinned at exactly `center.z` — the prior code only ever widened Z from `majorAxisEndpoint.z`
 * itself, never from `extrusion`.
 *
 * @codex T1 arm(c) round-22 P2 finding (F1) — round-21's fix ABOVE widened Z but left X/Y computed
 * from the OLD XY-only `ellipseExtentPoints2D(majorAxisEndpoint.x, majorAxisEndpoint.y, …)`, which
 * silently assumes the minor axis is a plain 90°-in-plane rotation of the major axis's `(x,y)` —
 * true ONLY for the default `(0,0,1)` extrusion. A genuinely tilted extrusion can move PART of the
 * minor axis's contribution OUT of X/Y and INTO Z: e.g. `majorAxisEndpoint=(10,0,0)`,
 * `extrusion=(0,1,0)` (a plane-normal 90° from the major axis) gives minor axis
 * `normalize(cross((0,1,0),(10,0,0)))·(10·ratio) = (0,0,−10·ratio)` — a PURE −Z vector, meaning
 * the ellipse's true plane is XZ, not XY: Y is CONSTANT at `center.y` (zero minor-axis
 * contribution left in Y) while Z gains the full range Y "lost". The old XY-only formula still
 * reported the full `[center.y − majorMag·ratio, center.y + majorMag·ratio]` Y range — an
 * OVER-report in this direction, but the SAME formula under-reports whenever the minor axis moves
 * a component INTO X or Y from a DIFFERENT starting orientation (the finding's own worked example:
 * `majorAxisEndpoint={x:1,y:0,z:10}` renders an ellipse whose X reaches ≈±10, not ≈±1). Both
 * directions of error trace to the same root cause: X/Y and Z were computed from two DIFFERENT,
 * mutually-inconsistent formulas (a 2D-plane-local one for X/Y, a separate amplitude pad for Z)
 * instead of ONE true 3D parametric curve.
 *
 * THE FIX: compute the ellipse's real 3D critical points DIRECTLY via
 * {@link ellipseExtentPoints3D}/{@link tessellateEllipse3D} — the SAME `P(t) = center +
 * majorAxis·cos(t) + minorAxis·sin(t)` parametrization the renderer draws (`majorAxis` = the FULL
 * 3D `majorAxisEndpoint` vector, `minorAxis` = `ellipseMinorAxis3D`'s real `extrusion × majorAxis`
 * direction) — replacing BOTH the old XY-only extent computation AND round-21's separate Z pad
 * with one provably-correct 3D computation. For the default extrusion + a planar major axis this
 * is byte-identical to the pre-fix X/Y (see {@link ellipseExtentPoints3D}'s own doc) — the
 * overwhelmingly common planar case is unaffected.
 *
 * @codex T1 arm(c) round-23 P2 finding — `thickness` extrudes along the ELLIPSE'S OWN `extrusion`
 * vector, not WCS Z: the prior code fed the (already fully-3D-correct) points through the generic
 * `withThicknessLocal` helper, which blindly adds `thickness` to each point's WCS Z — correct for a
 * genuine OCS-planar kind (Circle/Arc/LWPOLYLINE, whose 2D-plane-local Z axis IS the extrusion, by
 * construction, before `aabb3FromLocalPoints` rotates it into WCS) but WRONG here, because
 * `ellipseLocal` already returns `extrusion: undefined` (round-2: these points are ALREADY full
 * WCS, never fed through a further OCS transform) — there is no "OCS-local Z" left for a blind
 * Z-add to correctly land on. A tilted `extrusion` (e.g. `{x:0,y:1,z:0}`) means the ellipse's TRUE
 * thickness direction is world Y, not world Z; the prior code invented a spurious WCS-Z pad while
 * reporting zero widening in the real direction. {@link withThicknessAlongExtrusion} is the exact
 * fix already established for this same "WCS-final points, but thickness still extrudes along a
 * possibly-tilted extrusion vector" shape (see its own doc, and `lineLocal`/`pointLocal`, the two
 * other WCS-final kinds in this module) — reused here verbatim rather than re-derived.
 */
export function ellipseLocal(e: EllipseEntity, dense = false): LocalPointSet {
  const center: Wcs3 = { x: e.center.x, y: e.center.y, z: Z(e.center) };
  const majorAxis3D: Wcs3 = { x: e.majorAxisEndpoint.x, y: e.majorAxisEndpoint.y, z: Z(e.majorAxisEndpoint) };
  const minorAxis3D = ellipseMinorAxis3D(majorAxis3D, e.extrusion, e.ratio);
  let points = ellipseExtentPoints3D(center, majorAxis3D, minorAxis3D, e.startParam, e.endParam);
  if (dense) {
    points = [...points, ...tessellateEllipse3D(center, majorAxis3D, minorAxis3D, e.startParam, e.endParam)];
  }
  return { points: withThicknessAlongExtrusion(points, e.thickness, e.extrusion), extrusion: undefined };
}

/**
 * `extrusion` is deliberately OMITTED (@codex T1 arm(c) round-2 P2 finding): `model.ts`'s
 * `splineEntitySchema` doc (SL-15) documents control/fit points as "full 3D WCS" — the SPLINE
 * `extrusion` (group 210/220/230) "only preserves the plane ORIENTATION of a planar spline
 * authored in a tilted UCS", a round-trip-only marker, NOT a transform to re-apply to the
 * already-WCS control points (re-applying it would rotate an imported planar-tilted spline's
 * bounds to the wrong WCS location — the identical class of bug as the MTEXT/Ellipse findings).
 */
export function splineLocal(e: SplineEntity): LocalPointSet {
  // @codex T1 arm(c) round-18 P2 finding: a FIT-POINTS-mode spline's fit points lie ON the
  // curve, not on the Bezier/control hull that CONTAINS it — with tangent-constrained cubic
  // interpolation the rendered curve can bow outside the raw fit points' own AABB. Derive the
  // REAL control-point hull via the same `fitPointsToControlPoints` conversion the renderer
  // itself uses for fit-point splines (`splineMath.ts::buildSplineBeziers`), so the bounds hull
  // matches what is actually drawn instead of under-reporting it.
  const src =
    e.controlPoints.length > 0
      ? e.controlPoints
      : fitPointsToControlPoints(e.fitPoints, e.startTangent, e.endTangent, e.degree).controlPoints;
  // Convex-hull-of-control-points containment is exact math (not a heuristic) — see module doc.
  // A rotation/transform of the convex hull's vertices still bounds the (identically-transformed)
  // curve, so NO compounding-rotation gap exists here (unlike Circle/Arc/Ellipse's discrete
  // cardinal-sample reduction) — `dense` is deliberately NOT threaded to this kind.
  return { points: src.map((p) => ({ x: p.x, y: p.y, z: p.z ?? 0 })), extrusion: undefined };
}

/** `dense` — see {@link circleLocal}'s doc (threaded into bulged-segment tessellation). */
export function lwpolylineLocal(e: LwpolylineEntity, dense = false): LocalPointSet {
  const base = polylineLocalPoints(e.vertices, e.closed, e.elevation, dense || !isDefaultExtrusion(e.extrusion));
  return { points: withThicknessLocal(base, e.thickness), extrusion: e.extrusion };
}

/** `dense` — see {@link circleLocal}'s doc (threaded into bulged-segment tessellation). */
export function revisionCloudLocal(e: RevisionCloudEntity, dense = false): LocalPointSet {
  const base = polylineLocalPoints(e.vertices, e.closed, e.elevation, dense || !isDefaultExtrusion(e.extrusion));
  return { points: withThicknessLocal(base, e.thickness), extrusion: e.extrusion };
}

/**
 * A heavy `POLYLINE`'s local critical points.
 *
 * Two genuinely different cases, discriminated by group 70 — collapsing them
 * would produce wrong boxes, not just imprecise ones:
 *
 *  - a **2D polyline** lives in the OCS plane at the entity's elevation, and
 *    its per-vertex bulges are real arcs, so it reuses the SAME
 *    `polylineLocalPoints` walk `LWPOLYLINE` and Revision Cloud use (one
 *    truth for bulge-arc extrema) and reports the entity's `extrusion`;
 *  - a **3D polyline, polygon mesh or poly-face mesh** carries per-vertex Z
 *    in WCS and has no bulges at all (Autodesk DXF reference: group 42 is a
 *    2D-polyline field), so its points are the vertices themselves and NO
 *    extrusion is reported — applying an OCS tilt to already-WCS vertices
 *    would move the box.
 *
 * A **poly-face mesh**'s `vertices` list additionally interleaves two record
 * kinds (@codex PR #785 round 1, F3): the real vertex POOL and the FACE
 * records, whose 10/20/30 is FILLER — AutoCAD writes (0, 0, 0) — because a
 * face record's body is its 71/72/73/74 indices, not a location (Autodesk DXF
 * reference, VERTEX group 70; ezdxf `DXFVertex.is_face_record`). Mapping those
 * fillers dragged the box back to the WCS origin for every mesh authored away
 * from it, blowing up fit-to-extents and the spatial broad phase. The FACE
 * records are dropped through the SAME one-truth flag test the render adapter
 * splits on (`dxfVertexSubtype`, re-exported by `./entityPolylineTableOle`
 * from the dxf-io definition the parser and writer read — never a second copy
 * of the bit arithmetic), so what is bounded is exactly what is drawn by
 * `polylineFromEntity.ts::splitPolyface`.
 *
 * A **SMOOTHED polygon mesh** (group 75 quadratic / cubic B-spline or Bezier)
 * splits its `vertices` the same way (@codex PR #785 round 18, P2): the
 * records AutoCAD draws are the GENERATED surface it stored at the group-73/74
 * densities (VERTEX group 70 bit 8), and the authored 71×72 net is retained
 * after them as the bit-16 control FRAME `SPLFRAME` shows only when it is ON.
 * A control net generally bounds WIDER than the surface it generates, so
 * bounding every record put off-surface points that nothing draws into the
 * document extents, MVIEW FIT and viewport membership — measured on the
 * round-17 witness shape (a 3×4 net fitted at 5×6, its net parked at
 * `(-1000, -1000, 250)`): this walk reported
 * `{-1000, -1000, 0} … {4, 5, 250}` for a surface occupying
 * `{0, 0, 0} … {4, 5, 0}`. The generated pool is selected through
 * {@link surfaceFitVertexIndices} — the SAME one truth `cad-core`'s
 * `polylineMeshTopology.ts` builds its drawn wire runs from, never a second
 * copy of the group-75 / bit-8 rule (that module owns only the 73×74 GRID
 * dimensioning on top of it, which a point set does not need).
 *
 * `dense` — see {@link circleLocal}'s doc (threaded into bulged-segment
 * tessellation, 2D case only).
 */
export function heavyPolylineLocal(e: PolylineEntity, dense = false): LocalPointSet {
  const subtype = polylineSubtype(e);
  if (subtype !== "2d") {
    // Only a poly-face mesh has FACE records, and only a polygon mesh can
    // carry a fitted surface; filtering either rule outside its own subtype
    // would diverge from what the render adapter and the picker walk (both of
    // which gate the two rules on exactly these subtypes).
    const fitted =
      subtype === "polygon-mesh" ? surfaceFitVertexIndices(e) : null;
    const records = fitted
      ? fitted.map((i) => e.vertices[i]!)
      : subtype === "polyface-mesh"
        ? e.vertices.filter((v) => dxfVertexSubtype(v.flags) !== "face-record")
        : e.vertices;
    const pts: Wcs3[] = records.map((v) => ({
      x: v.location.x,
      y: v.location.y,
      z: v.location.z ?? 0,
    }));
    // Thickness on a 3D polyline / mesh extrudes along the entity's own
    // extrusion vector, which for these subtypes is the WCS normal.
    return {
      points: withThicknessAlongExtrusion(pts, e.thickness, e.extrusion),
      extrusion: undefined,
    };
  }
  const elevation = e.elevation.z ?? 0;
  // `bulge ?? 0`, NOT a conditional spread: `polylineLocalPoints` feeds
  // `v1.bulge` straight into `bulgeArc`, whose "is this segment straight?"
  // guard is `Math.abs(bulge) < EPS` — and `Math.abs(undefined)` is NaN, which
  // fails that comparison and poisons the whole point set with NaN (an ABSENT
  // bulge then produced a null bounding box). LWPOLYLINE never hit this because
  // its schema defaults `bulge` to 0; the heavy polyline's is genuinely
  // optional, so the default is applied here.
  const flat = e.vertices.map((v) => ({
    x: v.location.x,
    y: v.location.y,
    bulge: v.bulge ?? 0,
  }));
  const base = polylineLocalPoints(
    flat as unknown as readonly PolylineVertex[],
    polylineIsClosed(e),
    elevation,
    dense || !isDefaultExtrusion(e.extrusion),
  );
  return { points: withThicknessLocal(base, e.thickness), extrusion: e.extrusion };
}

/**
 * An `OLE2FRAME`'s local points: the two WCS corners of the frame plus the
 * other two rectangle corners, so a box is reported rather than a diagonal.
 *
 * The embedded OLE payload is deliberately not decoded (see
 * `oleFrameEntitySchema`'s doc), and it cannot change these bounds anyway —
 * AutoCAD clips the OLE picture to exactly this frame, so the frame IS the
 * entity's extent whether or not the raster is drawn.
 */
export function oleFrameLocal(e: OleFrameEntity): LocalPointSet {
  const a = e.upperLeft;
  const b = e.lowerRight;
  return {
    points: [
      { x: a.x, y: a.y, z: a.z ?? 0 },
      { x: b.x, y: a.y, z: a.z ?? 0 },
      { x: b.x, y: b.y, z: b.z ?? 0 },
      { x: a.x, y: b.y, z: b.z ?? 0 },
    ],
    extrusion: undefined,
  };
}

export function wipeoutLocal(e: WipeoutEntity): LocalPointSet {
  // WIPEOUT carries no elevation/thickness/extrusion field on this model — genuinely flat WCS-Z=0.
  return { points: e.vertices.map((v) => ({ x: v.x, y: v.y, z: 0 })), extrusion: undefined };
}

/**
 * `apps/web/…/scene/cannoscale/useCannoscaleResolver.ts::asRenderMultiplier` re-applied here
 * (`drawing-model` cannot import `apps/web` — wrong dependency direction, same
 * `wcsBoundsMath.ts::guardInsertScale` precedent this module family already follows): converts an
 * annotative context's `scaleFactor` (paperUnits/drawingUnits, e.g. 0.02 for "1:50") into the
 * render-multiplier applied to a MASTER (paper-space) height field (`displayed = paper ×
 * multiplier`). Returns 1 (no-op) for a zero/non-finite `scaleFactor` — the render's own defensive
 * fallback so a corrupt `AcDbScale` context never collapses that context's box to a point.
 *
 * Exported (@codex T1 arm(c) round-25 P2 finding, Fix 3): `wcsBoundsBlock.ts`'s ATTDEF
 * annotative-context union (its own `kind: "attribute"` contexts, same `scaleFactor` shape) needs
 * the IDENTICAL conversion — reused rather than re-derived a second time.
 */
export function annotativeScaleMultiplier(scaleFactor: number): number {
  return Number.isFinite(scaleFactor) && scaleFactor !== 0 ? 1 / scaleFactor : 1;
}

/**
 * @codex T1 arm(c) round-23 P2 finding (F8) — REAL FIX, closing the round-22 "documented
 * limitation" above (Codex re-flagged the doc-only resolution on the next HEAD: a doc comment
 * alone does not close a real under-report). This module's own "provably conservative, never
 * under-report" convention (see `circleLocal`/`arcLocal`/`ellipseLocal`'s `dense` tessellation —
 * over-approximate rather than guess which further transform applies) extends naturally here: a
 * VIEWPORT-AGNOSTIC document-global facet cannot know which single `e.annotativeContexts[]` entry
 * a given viewport has active (the round-22 doc's architectural reasoning is still correct — see
 * `mtextLocal`'s doc for the shared MTEXT half), so instead of picking one, this UNIONS the master
 * box with EVERY context's own resolved box. That guarantees the reported bounds cover the visible
 * glyphs at EVERY viewport's active scale simultaneously, with no need to resolve "the" active one.
 *
 * Each `kind: "text"` context (`annotative.ts::annotScaleTextContextSchema`) carries its OWN
 * `insertion`/`alignment` (optional)/`rotation` — the SAME 3 fields `resolveTypedAnnotation`
 * (`annotativeResolve.ts`) substitutes for the master baseline at render time
 * (`textFromEntity.ts`: `anno.insertion?.x ?? entity.insertionPoint.x`, `anno.rotation ?? …`,
 * `positiveOr(entity.height, 2.5) * anno.effectiveScale` where `effectiveScale =
 * asRenderMultiplier(ctx.scaleFactor)`, mirrored here by {@link annotativeScaleMultiplier}). This
 * builds ONE box per context — master fields + THIS context's insertion/alignment/rotation/scaled
 * height, all other fields (contents/widthFactor/justification/mirror/oblique — none of which vary
 * per scale on this schema) verbatim from the entity — via the SAME `textJustifiedLocalBox` the
 * master box already uses, then unions every corner into the returned point set.
 *
 * The OCS-local 2D corner set itself is {@link textLocalCorners} — exported (Codex PR #784
 * round 3 P1 "TEXT child extents in definition bounds") so `@dxf-ai-editor/cad-core`'s
 * `leafBounds.ts::textBounds` unions the IDENTICAL justified glyph box (master + every annotative
 * context) into a block definition's footprint instead of the anchor-only
 * `viewportMembership.ts::textBounds` pair — one truth, the same cross-package reuse precedent as
 * `constantAttdefLocalCorners` / `mtextBoxSize`. This function only adds the per-kind thickness
 * extrusion + OCS marker on top of those corners.
 */
export function textLocal(e: TextEntity): LocalPointSet {
  const base: Wcs3[] = textLocalCorners(e).map((p) => ({ x: p.x, y: p.y, z: 0 }));
  return { points: withThicknessLocal(base, e.thickness), extrusion: e.extrusion };
}

/**
 * The OCS-local 2D corners of a single-line TEXT's rendered glyph box — the master
 * `textJustifiedLocalBox` (justification-aware anchor, mirror/oblique, height/widthFactor
 * `positiveOr` fallbacks) UNION'd with one box per `kind: "text"` annotative context (that
 * context's own insertion/alignment/rotation and per-scale height) — see {@link textLocal}'s doc
 * for the full derivation. No thickness, no OCS: the caller applies those (or, for a planar 2D
 * AABB consumer such as cad-core, neither).
 */
export function textLocalCorners(e: TextEntity): Local2[] {
  const masterBox = textJustifiedLocalBox({
    insertionPoint: e.insertionPoint,
    alignmentPoint: e.alignmentPoint,
    height: e.height,
    rotation: e.rotation,
    contents: e.contents,
    widthFactor: e.widthFactor,
    hJustification: e.hJustification,
    vJustification: e.vJustification,
    generationFlags: e.generationFlags,
    obliqueAngle: e.obliqueAngle,
  });
  const corners: Local2[] = [...masterBox.corners];
  for (const ctx of e.annotativeContexts ?? []) {
    if (ctx.kind !== "text") continue;
    const height = positiveOr(e.height, 2.5) * annotativeScaleMultiplier(ctx.scaleFactor);
    const ctxBox = textJustifiedLocalBox({
      insertionPoint: ctx.insertion,
      alignmentPoint: ctx.alignment,
      height,
      rotation: ctx.rotation,
      contents: e.contents,
      widthFactor: e.widthFactor,
      hJustification: e.hJustification,
      vJustification: e.vJustification,
      generationFlags: e.generationFlags,
      obliqueAngle: e.obliqueAngle,
    });
    corners.push(...ctxBox.corners);
  }
  return corners;
}

/**
 * MTEXT geometry is documented as ALREADY-WCS (`model.ts`'s `mtextEntitySchema` doc: "MTEXT
 * geometry is WCS, but it CARRIES an extrusion attribute that AutoCAD/ezdxf use to encode a
 * mirrored-text det=−1 HANDEDNESS marker ((0,0,−1) ⇒ mirrored, see MIRRTEXT=1 in
 * `command-engine::mirrorTextFull`)" — unlike every OTHER OCS-planar kind (Text/Circle/…), MTEXT's
 * `extrusion` is NOT a genuine "which plane is this drawn in" transform. Feeding it into the
 * Arbitrary Axis Algorithm (as `aabb3FromLocalPoints` would, given a truthy `extrusion`) would
 * apply a real 3D rotation to coordinates that are already final WCS, reporting the box on the
 * WRONG side of the drawing for a mirrored/imported MTEXT (@codex T1 arm(c) P2 finding). The
 * mirror flag affects glyph ORIENTATION only, never the anchor-relative box extent, so
 * `extrusion` is deliberately OMITTED here (always the identity transform for MTEXT).
 *
 * @codex T1 arm(c) round-23 P2 finding (F8) — REAL FIX, same union strategy as {@link textLocal}'s
 * own doc above (read it first for the full architectural reasoning: a viewport-agnostic
 * document-global facet has no single "the" active context, so it unions every context's box with
 * the master's rather than picking one). Each `kind: "mtext"` context
 * (`annotative.ts::annotScaleMtextContextSchema`) carries its own `insertion`/`rotation`/optional
 * `attachment` — mirroring `mtextFromEntity.ts`'s render-time overrides: `anno.insertion?.x ??
 * entity.insertionPoint.x`, `anno.rotation` UNCONDITIONALLY supersedes the direction-derived angle
 * when a context matched (no `mtextEffectiveAngleDeg` re-derivation — the context's `rotation` IS
 * the final render angle), and `entity.nominalHeight * anno.effectiveScale`.
 *
 * WIDTH: MTEXT's per-scale context carries no `horizontalWidth`/`verticalHeight` override (those
 * are AutoCAD-computed master-only groups 42/43, always paper-space per the annotative
 * "master fields are paper-space, scaled `1/scaleFactor` per active representation" convention —
 * see `useCannoscaleResolver.ts::asRenderMultiplier`'s own doc). Those two groups are therefore
 * scaled, and the CONTENT estimate is re-evaluated at the context's own render height
 * (`nominalHeight × effectiveScale` — exactly what `mtextFromEntity.ts` seeds the parser with).
 * The two agree for every RELATIVE inline code, because the estimate is homogeneous of degree 1
 * in `nominalHeight`; they part company on an ABSOLUTE `\H<n>;`, which the parser uses to REPLACE
 * the seeded height, so that run paints at `n` world units in every context and must not be
 * multiplied by the context scale (@codex PR #794 round 7 P2 — see {@link mtextLocalCorners}).
 */
export function mtextLocal(e: MtextEntity): LocalPointSet {
  const corners = mtextLocalCorners(e);
  return { points: corners.map((p) => ({ x: p.x, y: p.y, z: 0 })), extrusion: undefined };
}

/**
 * The OCS-local 2D corners of an MTEXT's rendered box — the master
 * `mtextAnchoredBoxCorners` (attachment-grid anchored, effective-angle rotated) UNION'd with one
 * box per `kind: "mtext"` annotative context (that context's own insertion/rotation/attachment and
 * per-scale-scaled size) — see {@link mtextLocal}'s doc for the full derivation. No thickness, no
 * OCS: MTEXT is already-WCS and its `extrusion` is a handedness marker only (see {@link mtextLocal}).
 *
 * Exported (Codex PR #784 round 8 P2 "Include annotative MTEXT contexts in block bounds") so
 * `@dxf-ai-editor/cad-core`'s `leafBounds.ts::mtextBounds` unions the IDENTICAL master + every
 * annotative context box into a block definition's footprint instead of the master-only box —
 * one truth, the SAME cross-package reuse the TEXT arm already established through
 * {@link textLocalCorners}. A block-child MTEXT whose active annotative context differs in
 * insertion/rotation/scale from the master (which `mtextFromEntity` renders) then stays inside the
 * INSERT metadata AABB that drives cache-direct culling, fit-to-extents and broad-phase selection.
 */
export function mtextLocalCorners(e: MtextEntity): Local2[] {
  const masterSize = mtextBoxSize(e);
  const angle = mtextEffectiveAngleDeg(e.xAxisDirection, e.rotation);
  // The SIDE extents are where the `\\Q` oblique enlargement and the left side
  // bearing (both already inside `masterSize.width`) actually land — without
  // them the box is the right SIZE on the wrong SIDE of the attachment anchor
  // and the painted vertices fall outside it (@codex PR #794 round 3 P2,
  // round 10 P2 finding B). `paintedAbove`/`paintedBelow` additionally let
  // the corner builder cover the mesh's OWN baseline-anchored rectangle,
  // which ignores group 71 entirely (round 10 P2 finding C).
  // The group-210 handedness bit the RENDERER turns into `mesh.scale.set(1, -1, 1)`
  // (`sceneTextMeshFactories.ts::applyMirrorScale`, fed by `mtextFromEntity.ts`'s own
  // `isReflectedOcs(entity.extrusion)`): read from the MODEL through the one-truth predicate,
  // so the painted rectangle the corner builder unions in flips with the mesh instead of being
  // reported on the wrong side of the baseline (@codex PR #794 round 11 P2, finding 3).
  const mirrored = isReflectedOcs(e.extrusion);
  const corners: Local2[] = [
    ...mtextAnchoredBoxCorners(e.insertionPoint, masterSize.width, masterSize.height, e.attachmentPoint, angle, masterSize, mirrored),
  ];
  for (const ctx of e.annotativeContexts ?? []) {
    if (ctx.kind !== "mtext") continue;
    const scale = annotativeScaleMultiplier(ctx.scaleFactor);
    // The context's box is `mtextBoxSize` RE-EVALUATED at that context's own
    // render height, not the master's box multiplied by the scale.
    //
    // Groups 42/43 are paper-space master fields and do scale uniformly (they
    // are passed scaled, and `mtextBoxSize` returns them verbatim), and so
    // does everything the ESTIMATE branch derives from a relative code — the
    // estimate is homogeneous of degree 1 in `nominalHeight` for `\H<n>x;`,
    // `\W`, `\T`, `\Q` and plain text alike, so for the overwhelming
    // majority this is arithmetically the master box times `scale`, as before.
    //
    // An ABSOLUTE `\H<n>;` is the exception that makes the multiplication
    // wrong, and it is the renderer that decides so: `mtextFromEntity.ts`
    // seeds `parseMTextContent` with `entity.nominalHeight ×
    // anno.effectiveScale` and `mtextParser.ts`'s `\H` branch REPLACES that
    // seed with the absolute world value (`isMultiplier ? state.heightWorld ×
    // val : val`), so the formatted run paints at `n` world units in EVERY
    // context. `\H2;A` in a 1:100 context paints at height 2; the old
    // master-box × 100 reported it at 200, a 100× over-report of exactly the
    // kind that collapses the LOD fit zoom (@codex PR #794 round 7 P2).
    // Re-deriving against `nominalHeight × scale` makes the absolute form's
    // factor `n / (nominalHeight × scale)` — the height the context really
    // paints — and keeps the relative forms where they were.
    //
    // The side + painted-interval extents come out of the SAME call, so they
    // and the upright column they are subtracted from are consistent by
    // construction rather than by a matching multiplication (round 4).
    const ctxSize = mtextBoxSize({
      contents: e.contents,
      nominalHeight: e.nominalHeight * scale,
      horizontalWidth: e.horizontalWidth * scale,
      verticalHeight: e.verticalHeight * scale,
      lineSpacingFactor: e.lineSpacingFactor,
    });
    corners.push(
      ...mtextAnchoredBoxCorners(
        ctx.insertion,
        ctxSize.width,
        ctxSize.height,
        ctx.attachment ?? e.attachmentPoint,
        ctx.rotation,
        ctxSize,
        // The handedness is the ENTITY's — an annotative context carries its own
        // insertion/rotation/scale, never its own extrusion, and the renderer applies the one
        // mirror flip to whichever representation it draws.
        mirrored,
      ),
    );
  }
  return corners;
}

/**
 * radians → degrees, for the ONE Multileader angle field
 * {@link centeredTextBoxLocal}'s `rotationDeg` parameter needs converted
 * before the call: `MultileaderEntity.textRotation` (+ its
 * `annotativeContexts[].textRotation` sibling) is stored in RADIANS
 * end-to-end (model ⇔ DXF, CONTEXT_DATA MTextData group 42 — the SAME
 * MTEXT-content-angle anomaly `apps/web/…/adapters/typed/
 * multileaderFromEntity.ts`'s own `radToDeg` doc comment documents for the
 * render path), unlike every OTHER angle this module's bounds builders
 * consume, which are plain decimal degrees. GitHub Codex review PR #558
 * round 2 P2 — the render adapter already converts at its own boundary;
 * this module's Multileader bounds callers (`wcsBounds.ts`/
 * `wcsBoundsBlock.ts`) previously passed the raw radians value straight to
 * `centeredTextBoxLocal`'s degrees-math `rotateDeg2D`, under-rotating the
 * reported box by a factor of `180/π` (an MLeader stored at `Math.PI/2`,
 * rendered at 90°, had its BOUNDS box rotate by only ~1.57°) — a long text
 * run could extend well outside the spatial box, missed by culling/pick.
 */
export function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/**
 * A MULTILEADER text-content box anchored at `at`, honoring an explicit DXF group 40 wrap width
 * (`textWidth > 0`) and group 45 line-spacing factor (`textLineSpacingFactor > 0`) — matches
 * `cad-core/multileaderContextBounds.ts::multileaderTextContentCorners` EXACTLY (still re-exported
 * from `cad-core/leafBounds.ts`; Codex PR #784 round 9 P2 re-converged that copy onto the
 * `Math.max(textWidth, contentEstimate)` rule of Fix 9 below, which it had not carried) (`drawing-model` cannot import
 * `cad-core` — dependency direction, see this module family's header — so the identical formula
 * is re-derived here rather than shared cross-package, the same precedent every other piece of
 * shared MINSERT/OCS math in this module already follows). Symmetric-about-anchor
 * (over-inclusive, never under-reports — cad-core's own documented convention): MLEADER content
 * has no group-71 attachment anchor of its own (`textAttachmentType`/`textAttachmentDirection`
 * is a distinct MLEADER-only system) to justify a one-sided box against.
 *
 * @codex T1 arm(c) round-10 P2 finding: the prior call site ignored `textWidth`/
 * `textLineSpacingFactor` entirely, using only a fixed 0.6-aspect glyph estimate — an MLEADER
 * with an explicit wrap width narrower/wider than the estimate was mis-bounded.
 *
 * @codex T1 arm(c) round-25 P2 finding (Fix 9): `textWidth > 0` no longer uses `textWidth` AS the
 * box width outright — it now WIDENS the content-length estimate, never SHRINKS below it
 * (`Math.max(textWidth, contentEstimate)`). Confirmed against BOTH live MLEADER renderers: the SVG
 * path (`apps/web/…/scene/renderMultileader.tsx`) uses `textWidth` ONLY to size an OPTIONAL
 * decorative frame `<rect>` (`textFrameDisplay`) — the actual `<text>`/`<tspan>` glyph run is drawn
 * with NO width-based wrap/clip at all (SVG text never wraps), so a long string with a SMALL
 * `textWidth` still renders glyphs that overflow well past the frame; and the scene3d path
 * (`../geom/multileaderGeometry.ts::buildMultileaderGeometry`, `MultileaderRawFields` — read the
 * type directly) carries NO `textWidth` field whatsoever — `raw.contents` feeds `parseMTextContent`
 * with no wrap-width argument at all, so the WebGL glyph run's real footprint is purely a function
 * of content length, never clipped to a wrap-width hint. Using `textWidth` as the box width when it
 * is SMALLER than the content-length estimate therefore reported a box narrower than what either
 * renderer actually draws — a real under-report. `textWidth` remains free to WIDEN the box (a real
 * DXF group-45 hint some potential future consumer might honor) but can never narrow it below the
 * content estimate. `textWidth === 0`/absent is UNCHANGED (content-length-only, exactly as before).
 */
export function centeredTextBoxLocal(
  at: { x: number; y: number },
  contents: string,
  height: number,
  rotationDeg: number,
  textWidth: number,
  textLineSpacingFactor: number,
): Wcs3[] {
  const lines = contents.split(/\\P|\n/);
  const lineCount = Math.max(1, lines.length);
  const longest = lines.reduce((m, l) => Math.max(m, l.length), 0);
  const lineSpacing = textLineSpacingFactor > 0 ? textLineSpacingFactor : 1;
  const halfY = lineCount * (height * lineSpacing);
  const contentEstimate = Math.max(longest * height * 0.6, height);
  const width = textWidth > 0 ? Math.max(textWidth, contentEstimate) : contentEstimate;
  const halfX = width / 2;
  const local = [
    { x: -halfX, y: -halfY },
    { x: halfX, y: -halfY },
    { x: halfX, y: halfY },
    { x: -halfX, y: halfY },
  ];
  return local.map((c) => {
    const r = rotateDeg2D(c.x, c.y, rotationDeg);
    return { x: at.x + r.x, y: at.y + r.y, z: 0 };
  });
}

/**
 * The effective LAST leader-line vertex an MLEADER node's dogleg jogs off of: both live renderers
 * (`scene/renderMultileader.tsx`, `scene3d/dimensions/multileaderToPrimitives.ts`) SYNTHESIZE an
 * implicit second vertex at `textLocation` when a leader line carries only ONE vertex (the common
 * MLEADER-command arrow-only case), then jog the dogleg off THAT synthesized landing point — so
 * the true jog anchor is `textLocation`, not the lone arrow vertex. The dogleg only ever jogs off
 * the node's LAST leader line (every leader line has ≥1 vertex — `mleaderLeaderLineSchema
 * .vertices.min(1)`). `undefined` when the node has no leader line to anchor to.
 *
 * ONE TRUTH shared with `packages/cad-core/leafBounds.ts` (imports this directly — `cad-core`
 * DEPENDS ON `drawing-model`, never the reverse; mirrors the established
 * `ellipsePointAtDecomposed`/`mtextBoxSize`/`Vec3` precedent, dup:check convergence).
 */
export function multileaderNodeLastVertex(
  e: Pick<MultileaderEntity, "textLocation">,
  node: Pick<MleaderLeaderNode, "lines">,
): { x: number; y: number } | undefined {
  const lastLine = node.lines[node.lines.length - 1];
  const verts = lastLine?.vertices;
  if (!verts || verts.length === 0) return undefined;
  if (verts.length === 1) return e.textLocation;
  return verts[verts.length - 1];
}

/**
 * The DOGLEG endpoint for one MLEADER node, given its LAST leader-line vertex (see
 * {@link multileaderNodeLastVertex}): direction is the node's own `doglegVector` (normalized)
 * when present, else a HORIZONTAL fallback signed toward `textLocation`; length is the
 * entity/annotative-resolved `doglegLength` (NOT a per-node override — see the round-21 doc
 * below). `null` when landing/dogleg is disabled or there is no vertex to jog from.
 *
 * ONE TRUTH shared with `packages/cad-core/leafBounds.ts` (imports this directly — same
 * dependency-direction rationale as {@link multileaderNodeLastVertex}) AND with
 * `packages/cad-core/src/grips.ts`/`selectLeaf.ts` (dogleg grip placement / crossing-select
 * hit-testing both call this same function) — all four consumers now agree with the ACTUAL
 * rendered dogleg length.
 *
 * @codex T1 arm(c) round-12 P2 finding: the prior MLEADER bounds never accounted for the dogleg
 * segment at all — a dogleg extending past the last leader-line vertex was under-reported.
 *
 * @codex T1 arm(c) round-21 P2 finding: a `MleaderLeaderNode.doglegLength` (LeaderData's own DXF
 * group 40 override — genuinely imported, non-synthetic data: `dxfBridge.entityConversion.ts`
 * populates it verbatim from the parsed context data, `if (entity.doglegLength) geom.doglegLength
 * = entity.doglegLength`) was previously PREFERRED over the entity-level value here — but BOTH
 * live renderers build every node's dogleg from the SAME single entity/annotative-resolved
 * `doglegLength` and NEVER read a per-node override at all: `scene/renderMultileader.tsx`'s
 * `doglegLength = ctx?.doglegLength ?? readMlNumberWithOverride(...)` (one value for the whole
 * entity) and `scene3d/adapters/geom/multileaderGeometry.ts::buildMultileaderGeometry`'s
 * `endX = last.x + (dx/mag) * raw.doglegLength` (same — `raw.doglegLength` is the ONE
 * `MultileaderRawFields.doglegLength`, never re-read per node). Preferring `node.doglegLength`
 * reported a bounds/grip/selection dogleg endpoint LONGER or SHORTER than the segment actually
 * drawn — this function must describe what is actually RENDERED, not a plausible-but-
 * unimplemented richer behavior (this module's "honest over misleading" policy applies to
 * over-PRECISION the same way it applies to over-inclusion elsewhere). Threading the per-node
 * override into the renderers instead would be a legitimate follow-up (a real, non-synthetic DXF
 * field currently dropped at render time) — tracked separately, NOT bolted on as a side effect of
 * this bounds-only fix.
 */
export function multileaderDoglegEnd(
  e: Pick<MultileaderEntity, "landingEnabled" | "doglegEnabled" | "doglegLength" | "textLocation">,
  node: Pick<MleaderLeaderNode, "doglegVector">,
  lastVertex: { x: number; y: number } | undefined,
): { x: number; y: number } | null {
  if (!e.landingEnabled || !e.doglegEnabled || !lastVertex) return null;
  const doglegLength = e.doglegLength;
  let dirX: number;
  let dirY: number;
  if (node.doglegVector) {
    const mag = Math.hypot(node.doglegVector.x, node.doglegVector.y) || 1;
    dirX = node.doglegVector.x / mag;
    dirY = node.doglegVector.y / mag;
  } else {
    // Horizontal fallback — same sign rule as the renderer.
    dirX = e.textLocation.x >= lastVertex.x ? 1 : -1;
    dirY = 0;
  }
  return { x: lastVertex.x + dirX * doglegLength, y: lastVertex.y + dirY * doglegLength };
}

/**
 * The DOGLEG-END → LANDING-GAP endpoint (`buildMultileaderGeometry`'s "landing" primitive):
 * `textLocation − unit(textLocation − doglegEnd) · landingGap`. Read verbatim from
 * `../geom/multileaderGeometry.ts` (the SAME shared builder both live renderers feed) — a narrow,
 * previously-uncomputed contribution {@link multileaderDoglegEnd} itself does not cover (that
 * helper mirrors only the DOGLEG segment's own endpoint formula, not the further landing-gap
 * segment past it). Normally interior to the already-unioned `{doglegEnd, textLocation}` segment
 * (both already separate unioned points), so this only WIDENS the box in the narrow case where
 * `landingGap` exceeds that segment's own length.
 *
 * @codex T1 arm(c) round-25 P2 finding (Fix 1) — added for the TOP-LEVEL `wcsBounds.ts
 * ::multileaderWcsBounds`/`multileaderContextLeaderPad` walk (both the master and every per-context
 * dogleg). EXPORTED here (moved out of `wcsBounds.ts`, round-26 P2 finding, Fix 3) so
 * `wcsBoundsBlock.ts::multileaderLeafLocal` — the BLOCK-DEFINITION-CHILD MLEADER walk — can reuse
 * the exact same formula: that path previously added only `multileaderDoglegEnd` (round-12), never
 * this further landing-gap segment, under-reporting a block-child MLEADER's resolved bounds
 * whenever its `landingGap` exceeds the dogleg-to-text distance, the identical gap the round-25 fix
 * already closed at the top level. Follows this file family's established "export the shared
 * helper, both call sites use it" pattern already used for {@link multileaderDoglegEnd}/
 * {@link multileaderNodeLastVertex}.
 */
export function multileaderLandingEnd(
  textLocation: { x: number; y: number },
  doglegEnd: { x: number; y: number },
  landingGap: number,
): { x: number; y: number } {
  const dx = textLocation.x - doglegEnd.x;
  const dy = textLocation.y - doglegEnd.y;
  const mag = Math.hypot(dx, dy) || 1;
  return { x: textLocation.x - (dx / mag) * landingGap, y: textLocation.y - (dy / mag) * landingGap };
}

/**
 * For an ALIGNED (`dimensionType===1`) DIMENSION, the render-projected dimension-line endpoints
 * (the dim line is offset PERPENDICULAR to the extension-origin chord, so it can lie far outside
 * the raw definition-point hull), PLUS the DIMEXE-extended extension-line far ends and
 * DIMEXO-offset near ends. `[]` for any other subtype (Linear's dim line is axis-aligned and
 * already inside the definition-point hull; Radius/Diameter/Angular/Ordinate carry no extension
 * origins). The dim-line point mirrors the renderer's own `defPoint.x || textMidpoint.x` fallback.
 *
 * ONE TRUTH shared with `packages/cad-core/leafBounds.ts::dimensionBoundsPoints` — `cad-core`
 * DEPENDS ON `drawing-model` (never the reverse), so this is the correct direction for math both
 * packages need (mirrors the established `ellipsePointAtDecomposed`/`mtextBoxSize`/`Vec3`
 * precedent — dup:check convergence, not a re-derivation).
 *
 * @codex T1 arm(c) round-12 P2 finding: the prior dimensionLocal unioned only the RAW
 * defPoint/extLine points — an ALIGNED dimension's actual rendered dim line/extension overshoot
 * was under-reported.
 */
export function alignedDimProjectedPoints(e: DimensionEntity, dimexo: number, dimexe: number): Local2[] {
  if (e.dimensionType !== 1 || !e.extLine1Point || !e.extLine2Point) return [];
  const x1 = e.extLine1Point.x;
  const y1 = e.extLine1Point.y;
  const x2 = e.extLine2Point.x;
  const y2 = e.extLine2Point.y;
  const dx = e.defPoint.x || e.textMidpoint.x;
  const dy = e.defPoint.y || e.textMidpoint.y;

  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const perpX = -(y2 - y1) / len;
  const perpY = (x2 - x1) / len;
  const offset = (dx - (x1 + x2) / 2) * perpX + (dy - (y1 + y2) / 2) * perpY;
  const dir = offset >= 0 ? 1 : -1;
  const d1x = x1 + perpX * offset;
  const d1y = y1 + perpY * offset;
  const d2x = x2 + perpX * offset;
  const d2y = y2 + perpY * offset;

  return [
    { x: d1x, y: d1y },
    { x: d2x, y: d2y },
    { x: d1x + perpX * dimexe * dir, y: d1y + perpY * dimexe * dir },
    { x: d2x + perpX * dimexe * dir, y: d2y + perpY * dimexe * dir },
    { x: x1 + perpX * dimexo * dir, y: y1 + perpY * dimexo * dir },
    { x: x2 + perpX * dimexo * dir, y: y2 + perpY * dimexo * dir },
  ];
}

/** `v` when finite, else `fallback` — package-local re-derivation of `dimensionHelpers.ts
 * ::dimensionStyleRowToValues`'s `numOr` (optional-safe: `dimStyle` itself may be absent). Applies
 * to DIMVARs with no positivity constraint (DIMEXO/DIMEXE/DIMGAP/DIMRND/DIMLFAC — 0 or a negative
 * DIMEXO is a legitimate, if unusual, DXF value). */
export function numOrLocal(v: number | undefined, fallback: number): number {
  return v !== undefined && Number.isFinite(v) ? v : fallback;
}

/** `v` when finite AND strictly positive, else `fallback` — re-derivation of `dimensionHelpers.ts
 * ::dimensionStyleRowToValues`'s `positiveOr`. Applies to DIMVARs the render clamps positive
 * (DIMASZ/DIMTXT — a zero/negative arrow size or text height is schema-VALID but the renderer
 * substitutes the default rather than drawing a degenerate/invisible marker). */
export function positiveOrLocal(v: number | undefined, fallback: number): number {
  return v !== undefined && Number.isFinite(v) && v > 0 ? v : fallback;
}

/**
 * The subtype's measurement-symbol PREFIX — matches `dimensionGeometry.ts::beginEmit`'s `prefix`
 * argument (`emitDiameter`/`emitRadius`'s call sites), via the SAME DXF group-70 low-nibble
 * mapping `resolveDimensionSubtype` uses. `undefined` for every other subtype (Linear/Aligned/
 * Ordinate carry no prefix; Angular's "°" is a trailing SUFFIX — {@link dimensionIsAngularSubtype}
 * — not a leading prefix).
 */
function dimensionMeasurementPrefix(dimensionType: number | undefined): string | undefined {
  const nibble = (dimensionType ?? 0) & 0x0f;
  if (nibble === 3) return "⌀"; // Diameter
  if (nibble === 4) return "R"; // Radius
  return undefined;
}

/** Whether this subtype's measurement text carries a trailing "°" (Angular / Angular-3pt — DXF
 * group-70 low nibble 2/5), matching `dimensionGeometry.ts::emitAngular`'s `text ? text + "°" : ""`. */
function dimensionIsAngularSubtype(dimensionType: number | undefined): boolean {
  const nibble = (dimensionType ?? 0) & 0x0f;
  return nibble === 2 || nibble === 5;
}

/**
 * The EXACT rendered measurement-text STRING for a DIMENSION whose `dimensionText` is empty/`<>`
 * — a full re-derivation of `apps/web/…/scene/dimensionHelpers.ts::formatDimMeasurement` (DIMLFAC
 * linear scale, DIMRND rounding, DIMLUNIT/DIMDEC unit formatting, subtype prefix, DIMPOST
 * prefix/suffix-with-`<>`-substitution) PLUS `computeToleranceInfo`'s "limits" suppression
 * (`DIMLIM` ⇒ the MAIN label is empty — a separate upper/lower tolerance pair renders instead,
 * out of this function's scope, see {@link dimensionLocal}'s own doc) and `emitAngular`'s
 * trailing "°" — @codex T1 arm(c) round-22 P2 finding (F2).
 *
 * `packages/drawing-model` cannot import `apps/web` (wrong dependency direction,
 * `.dependency-cruiser.cjs` forbids it), so the SUBTYPE-PREFIX/DIMPOST/DIMLIM/"°"-suffix
 * orchestration stays a package-local re-derivation. The two lowest-level pieces (DIMRND
 * rounding, DIMLUNIT/DIMDEC unit formatting) are pure number-in/string-out with NO further
 * dependency at all — `./dimensionMeasurementFormat.ts`'s `applyDimRounding`/
 * `formatDimValueByUnit` — so those converge to a SINGLE shared implementation `apps/web`
 * imports FROM (`apps/web` depends on `packages/drawing-model`, the normal app→package
 * direction; jscpd flagged the two independent re-derivations as duplication — see that
 * module's own header for the full rationale).
 *
 * A REAL non-empty, non-`"<>"` `dimensionText` never reaches this function — the renderer draws
 * that literal string verbatim (`formatDimMeasurement`'s own first branch), and so does
 * {@link dimensionLocal}'s caller (only invokes this for the empty/`"<>"` case).
 *
 * @codex T1 arm(c) round-25 P2 finding (Fix 8) — `dimlfac`/`dimrnd`/`dimlunit`/`dimdec`/`dimpost`
 * NO LONGER consult `e.styleOverrides` at all (previously `e.styleOverrides?.field ?? ...`, giving
 * a per-entity DSTYLE-XDATA override FIRST precedence). Confirmed by re-reading
 * `apps/web/…/scene3d/adapters/typed/dimensionFromEntity.ts`'s own module header verbatim:
 * "golden parity with the LIVE bag render requires resolving DIMVARs from the named
 * `DimensionStyle` TABLE row (`ctx.dimensionStyles`), never from `entity.styleOverrides` (a
 * different, currently render-unwired channel)" — confirmed independently by reading
 * `dimensionFromEntity.ts` itself (no `entity.styleOverrides` reference anywhere in the file) and
 * `dimensionGeometry.ts::beginEmit`/`formatDimMeasurement`'s call chain (both take a resolved
 * `DimStyleValues`, sourced EXCLUSIVELY from `resolveDimensionStyleRow`/`dimensionStyleRowToValues`
 * — the named DIMSTYLE-table row — never from the entity). The legacy bag path
 * (`dimensionToPrimitives.ts`) is the only OTHER live DIMENSION render path, and it calls
 * `readDimStyle(entity.properties)` — a STRING-KEYED bag, not a typed `DimensionEntity` — so
 * `readDimStyle`'s typed `styleOverrides`-reading branch is dead code for every real document
 * entity (only exercised by unit tests calling it directly). `entity.styleOverrides` therefore has
 * NO EFFECT WHATSOEVER on what either live renderer actually draws for these 5 fields — a value
 * this facet gave first precedence to could make the reported measurement text SHORTER than what
 * the DIMSTYLE-table-driven renderer actually draws, under-reporting the width-driven text box.
 * The prior doc's "matches cad-core's own documented styleOverrides-aware convention" framing
 * (round-22) was based on an incomplete picture of which render path is actually live — corrected
 * here.
 *
 * @codex T1 arm(c) round-26 P2 finding — `dimlim` (the "limits" suppression flag) is NOW PART OF
 * this same precedence correction too (the 11th field), reversing round-25's own doc note that it
 * was "DELIBERATELY UNCHANGED". That note was itself a mistake: `dimlim` has the IDENTICAL
 * zero-effect-on-the-live-renderer property the round-25 doc above already established for the
 * other 10 fields — the SAME `dimensionFromEntity.ts`/`dimensionGeometry.ts::beginEmit` evidence
 * (`computeToleranceInfo(raw.measurement, style)`, called with the resolved `DimStyleValues` only,
 * never `entity.styleOverrides`) applies here without exception. Giving `e.styleOverrides?.dimlim`
 * first precedence could make THIS function wrongly suppress the label (`dimlim=true` from a
 * per-entity override the renderer never applies) while the live renderer still draws the full
 * formatted measurement — under-reporting the box to the near-zero "limits mode" width for a
 * dimension that actually renders full text, the same under-report class every other field in this
 * precedence family exists to prevent.
 */
function formattedMeasurementText(e: DimensionEntity, dimStyle: DimensionStyle | undefined): string {
  const dimlim = dimStyle?.DIMLIM ?? false;
  if (dimlim) return ""; // "limits" mode suppresses the MAIN label (separate upper/lower text — out of scope here).
  const dimlfac = numOrLocal(dimStyle?.DIMLFAC, 1);
  const dimrnd = numOrLocal(dimStyle?.DIMRND, 0);
  const dimlunit = Math.round(numOrLocal(dimStyle?.DIMLUNIT, 2));
  // `Math.min(…, 100)` — `toFixed`/`toExponential` throw a RangeError past 100 fraction digits
  // (ECMA-262); the render's own `formatDimValueByUnit` has no such upper clamp (a genuinely
  // malformed DIMDEC could crash IT too), but a BOUNDS computation must never throw for
  // out-of-spec DXF input — this is a defensive robustness addition, not a fidelity deviation
  // (no real-world DIMDEC ever approaches this bound).
  const dimdec = Math.max(0, Math.min(100, Math.round(numOrLocal(dimStyle?.DIMDEC, 4))));
  const dimpost = dimStyle?.DIMPOST ?? "";

  const scaled = applyDimRounding(e.measurement * dimlfac, dimrnd);
  const value = formatDimValueByUnit(scaled, dimlunit, dimdec);
  const prefix = dimensionMeasurementPrefix(e.dimensionType);
  const prefixed = prefix ? `${prefix}${value}` : value;
  const posted = dimpost ? (dimpost.includes("<>") ? dimpost.replace("<>", prefixed) : `${prefixed}${dimpost}`) : prefixed;
  return dimensionIsAngularSubtype(e.dimensionType) && posted ? `${posted}°` : posted;
}

/**
 * @codex T1 arm(c) round-22 P2 finding — threads the document's DIMSTYLE table (`dimStyle`, the
 * ALREADY-RESOLVED style for `e.styleName`, DXF group 3) into every DIMVAR fallback chain below.
 * Resolution happens at the CALL SITE (`wcsBounds.ts`'s `case "Dimension":` /
 * `wcsBoundsBlock.ts`'s `childLocalPointSet`'s nested `case "Dimension":`, both via
 * `wcsBoundsBlock.ts::resolveDimensionStyleFromContext` — a named-lookup with a "Standard"
 * fallback, mirroring the renderer's own `resolveDimensionStyleRow`), NOT inside this leaf
 * function — keeps this function simple and matches the "leaf builders take already-resolved
 * inputs" pattern this module already follows (e.g. this function never resolves a block
 * DEFINITION itself either). Previously this
 * function had NO DIMSTYLE-table context at all: a Dimension using a NAMED style with non-default
 * DIMASZ/DIMTXT/DIMEXE/DIMEXO/DIMGAP/DIMDEC/DIMPOST and no per-entity `styleOverrides` (XData
 * DIMVAR overrides) silently fell straight through to AutoCAD's hardcoded default, understating
 * the bounds padding for every dimension actually using a real, non-default named style.
 * `cad-core/leafBounds.ts::dimensionBoundsPoints` still carries the OLD "no DIMSTYLE-table
 * context" constraint — a SEPARATE, out-of-scope facet this fix does not touch (a different
 * consumer with its own context-threading follow-up, not fixed as a side effect here).
 */
/**
 * @codex T1 arm(c) round-25 P2 finding (Fix 6) — the SUBTYPE-SPECIFIC label anchor each `emit*`
 * function in `dimensionGeometry.ts` actually positions `textPos` at — `e.textMidpoint` ALONE (the
 * anchor `dimensionLocal`'s label box used, below) is only the raw DXF group-11 point, which the
 * renderer treats as a mere FALLBACK component of `dimLine` (`dimLine.x = defPt.x || textMid.x`,
 * per `dimensionFromEntity.ts`'s own field-mapping doc), not the actual glyph position for 5 of
 * the 6 subtypes. Every formula below is read verbatim from the corresponding `emit*` function
 * body (`apps/web/…/scene3d/adapters/geom/dimensionGeometry.ts`):
 *   - Linear (`emitLinear`): `{x: dx || (x1+x2)/2, y: dy + dimgap + textHeight*0.3}`.
 *   - Aligned (`emitAligned`): the dim-line-perpendicular-offset midpoint + `dimgap+height*0.3` pad
 *     along the SAME perpendicular the dim line itself is offset along.
 *   - Angular / Angular-3pt (`emitAngular`): `vx/vy + (arcRadius+dimgap+height*0.3)` along the
 *     arc's own mid-angle direction.
 *   - Diameter (`emitDiameter`): `{x: dx || (x1+x2)/2, y: (dy || (y1+y2)/2) + dimgap+height*0.3}`.
 *   - Radius (`emitRadius`): `{x: (dx+x1)/2, y: (dy+y1)/2 + dimgap+height*0.3}`.
 *   - Ordinate (`emitOrdinate`): `{x: dx, y: dy + dimgap+height*0.3}`.
 * `x1/y1`/`x2/y2` default to `{0,0}` when `extLine1Point`/`extLine2Point` are absent (the SAME
 * `ext1 = entity.extLine1Point ?? {x:0,y:0}` default `dimensionFromEntity.ts` applies) — every
 * formula is therefore fully computable from fields this schema ALWAYS carries, no subtype is
 * "under-covered by missing geometry" for this model. `nibble` mirrors `resolveDimensionSubtype`'s
 * own out-of-range clamp (any nibble outside 0-6 collapses to Linear) —
 * {@link dimensionMeasurementPrefix}/{@link dimensionIsAngularSubtype} already decode the SAME low
 * nibble for a related purpose, reused here (not re-derived a third time).
 *
 * The caller UNIONS a box built at this anchor with the EXISTING `e.textMidpoint`-anchored box
 * (never replaces it) — this module's own established "provably conservative, never under-report"
 * convention (matches `textLocal`/`mtextLocal`'s round-23 F8 context union, `multileaderWcsBounds`'s
 * round-24/round-25 context unions, etc.): even though every formula above is fully computable from
 * always-present fields, keeping `textMidpoint` as an ADDITIONAL anchor costs nothing (extra points
 * only ever WIDEN a reported AABB) and defends against any subtle formula mismatch rather than
 * silently narrowing coverage on a replace-only strategy.
 */
function dimensionLabelAnchor(e: DimensionEntity, dimgap: number, textHeight: number): Local2 {
  const x1 = e.extLine1Point?.x ?? 0;
  const y1 = e.extLine1Point?.y ?? 0;
  const x2 = e.extLine2Point?.x ?? 0;
  const y2 = e.extLine2Point?.y ?? 0;
  const dx = e.defPoint.x || e.textMidpoint.x;
  const dy = e.defPoint.y || e.textMidpoint.y;
  const pad = dimgap + textHeight * 0.3;
  const nibbleRaw = (e.dimensionType ?? 0) & 0x0f;
  const nibble = nibbleRaw >= 0 && nibbleRaw <= 6 ? nibbleRaw : 0;
  switch (nibble) {
    case 1: {
      // Aligned — `emitAligned`'s `textMidX`/`textMidY`.
      const lineAngle = Math.atan2(y2 - y1, x2 - x1);
      const perpX = -Math.sin(lineAngle);
      const perpY = Math.cos(lineAngle);
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      const offsetDist = (dx - midX) * perpX + (dy - midY) * perpY;
      const dim1x = x1 + perpX * offsetDist;
      const dim1y = y1 + perpY * offsetDist;
      const dim2x = x2 + perpX * offsetDist;
      const dim2y = y2 + perpY * offsetDist;
      return { x: (dim1x + dim2x) / 2 + perpX * pad, y: (dim1y + dim2y) / 2 + perpY * pad };
    }
    case 2:
    case 5: {
      // Angular / Angular-3pt — `emitAngular`'s `textPos`.
      const tx = e.thirdPoint?.x ?? 0;
      const ty = e.thirdPoint?.y ?? 0;
      const vx = tx || dx;
      const vy = ty || dy;
      const angle1 = Math.atan2(y1 - vy, x1 - vx);
      const angle2 = Math.atan2(y2 - vy, x2 - vx);
      const arcRadius = Math.hypot(dx - vx, dy - vy) || Math.min(Math.hypot(x1 - vx, y1 - vy), Math.hypot(x2 - vx, y2 - vy)) * 0.7;
      const midAngle = angle1 + ((((angle2 - angle1) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) / 2;
      const r = arcRadius + pad;
      return { x: vx + r * Math.cos(midAngle), y: vy + r * Math.sin(midAngle) };
    }
    case 3:
      // Diameter — `emitDiameter`'s `textPos`.
      return { x: dx || (x1 + x2) / 2, y: (dy || (y1 + y2) / 2) + pad };
    case 4:
      // Radius — `emitRadius`'s `textPos`.
      return { x: (dx + x1) / 2, y: (dy + y1) / 2 + pad };
    case 6:
      // Ordinate — `emitOrdinate`'s `textPos`.
      return { x: dx, y: dy + pad };
    case 0:
    default:
      // Linear — `emitLinear`'s `textPos`.
      return { x: dx || (x1 + x2) / 2, y: dy + pad };
  }
}

/**
 * The subtype BASE angle (degrees) `dimensionGeometry.ts`'s emitters add
 * `raw.textRotationOverride` (`entity.textAngleRelativeToDimLine`, DXF group
 * 53) on top of — `emitAligned`'s `const textRot = style.dimtih ? 0 :
 * (lineAngle * 180) / Math.PI;` for `nibble===1` (Aligned), and a hardcoded
 * `0` base for every OTHER subtype (`emitLinear`/`emitAngular`/
 * `emitDiameter`/`emitRadius`/`emitOrdinate` all pass `raw.
 * textRotationOverride` DIRECTLY as `rotation`, confirmed by reading each in
 * full — none branches on any DIMVAR for this argument).
 *
 * EXPORTED (GitHub Codex review PR #558 finding — dimension bounds gap) so
 * `apps/web`'s MIRROR reducer can compute the SAME base angle from the
 * entity's PRE-mirror vs. POST-mirror `extLine1Point`/`extLine2Point` to
 * derive the readable-mode (MIRRTEXT=0) `textAngleRelativeToDimLine` offset
 * from the mirrored base + desired final readable angle — ONE shared
 * formula, not a re-derived parallel truth in the reducer.
 *
 * `dimtih` defaults to `true` when the resolved style/table row is absent or
 * doesn't carry it — matches `model.ts`'s own `dimensionStyleSchema`:
 * `DIMTIH: z.boolean().default(true)`.
 */
export function dimensionSubtypeBaseAngleDeg(
  nibble: number,
  dimtih: boolean,
  ext1: { x: number; y: number } | undefined,
  ext2: { x: number; y: number } | undefined,
): number {
  if (nibble !== 1 || dimtih) return 0;
  const x1 = ext1?.x ?? 0;
  const y1 = ext1?.y ?? 0;
  const x2 = ext2?.x ?? 0;
  const y2 = ext2?.y ?? 0;
  return (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
}

/** The dimension subtype nibble (DXF group 70 low nibble, clamped to the 7
 *  known subtypes — matches `dimensionGeometry.ts`'s own `dimensionType &
 *  0x0f` extraction, defaulting an out-of-range value to Linear (0)). */
export function dimensionSubtypeNibble(dimensionType: number | undefined): number {
  const nibbleRaw = (dimensionType ?? 0) & 0x0f;
  return nibbleRaw >= 0 && nibbleRaw <= 6 ? nibbleRaw : 0;
}

/**
 * @codex T1 arm(c) round-26 P2 finding (Fix 1) — the label-text ROTATION `dimensionGeometry.ts
 * ::emitAligned` actually feeds `emitMeasurementText`, generalised (GitHub Codex review PR #558
 * finding) to EVERY subtype's ACTUAL rendered rotation: {@link dimensionSubtypeBaseAngleDeg} PLUS
 * `entity.textAngleRelativeToDimLine` (DXF group 53 — every emitter adds this on top of its own
 * base, `dimensionGeometry.ts` confirmed by reading each emitter in full). Previously this read the
 * UNRELATED `e.textRotation` (DXF group 52, ezdxf's `oblique`) for non-Aligned subtypes and omitted
 * the group-53 offset entirely for Aligned — a Dimension whose renderer draws its label at a
 * nonzero group-53 angle (an authored override, or a MIRRTEXT-mirrored one) could draw outside the
 * box this bounds gate reported.
 *
 * Per round-25's Fix 8 finding (`formattedMeasurementText`'s own doc), `e.styleOverrides` is NEVER
 * consulted for a DIMVAR here either — it has zero effect on either live DIMENSION renderer.
 */
function dimensionSubtypeTextRotationDeg(e: DimensionEntity, dimStyle: DimensionStyle | undefined): number {
  const nibble = dimensionSubtypeNibble(e.dimensionType);
  const dimtih = dimStyle?.DIMTIH ?? true;
  const base = dimensionSubtypeBaseAngleDeg(nibble, dimtih, e.extLine1Point, e.extLine2Point);
  return base + (e.textAngleRelativeToDimLine ?? 0);
}

/**
 * The dimension label's own EXACT rendered box shape — `dimensionGeometry.ts::emitMeasurementText`
 * draws the text primitive with `alignment: "baseline-center"` (hardcoded, unconditional, identical
 * for every subtype) and extends its OWN bbox to EXACTLY `[position.x - halfX, position.y]` …
 * `[position.x + halfX, position.y + textHeight]` (`halfX = Math.max(content.length * textHeight *
 * 0.3, textHeight)`) — a BOTTOM-anchored (Y range starts AT the anchor, never below or centered on
 * it), horizontally-CENTERED rectangle. This does NOT follow `DimensionEntity.attachmentPoint`'s
 * MTEXT-style 1-9 attachment grid at all — confirmed by reading `emitMeasurementText` in full,
 * `alignment` is a hardcoded literal, never a function of any DIMVAR or entity field — so
 * `attachmentPoint` has NO EFFECT on either live DIMENSION renderer (grepped both
 * `dimensionFromEntity.ts` and the legacy `dimensionToPrimitives.ts` — neither reads
 * `attachmentPoint` at all), the SAME "a field the renderer ignores must not drive this module's
 * box" principle round-25's Fix 8 established for `styleOverrides`.
 *
 * @codex T1 arm(c) round-26 P2 finding (Fix 2) — {@link dimensionLocal}'s label boxes previously
 * called the generic `mtextAnchoredBoxCorners` (MTEXT's 1-9 attachment-grid convention, keyed off
 * `e.attachmentPoint`) — for the schema's own DEFAULT dimension `attachmentPoint` (5, Middle-Center),
 * that produces a box SYMMETRIC around the anchor's Y (`[anchor.y - height/2, anchor.y +
 * height/2]`), under-reporting the box's real top edge relative to what the renderer actually draws
 * (`anchor.y + textHeight`, not `anchor.y + textHeight/2`) — and, for the schema's OWN default,
 * mis-shapes the box on BOTH edges (bottom too high, top too low) since the renderer never centers
 * at all. Building the box from the renderer's own exact `[0, textHeight]` Y range instead removes
 * this whole class of mismatch, for every subtype's box (both the master `e.textMidpoint`-anchored
 * box AND every subtype's own {@link dimensionLabelAnchor} pose, from round-25's Fix 6 union).
 */
function dimensionMeasurementTextBoxCorners(
  anchor: Local2,
  halfWidth: number,
  height: number,
  rotationDeg: number,
  mirrored: boolean,
): Local2[] {
  // GitHub Codex review PR #558 round 2 (round-5 local read-only follow-up)
  // P2 — this box now follows the rendered group-53 rotation
  // (`dimensionSubtypeTextRotationDeg`), so its HANDEDNESS must match the
  // renderer too. `dimensionGeometry.ts::emitMeasurementText`'s own doc
  // (the render-path twin of this bounds calc): a MIRRTEXT=1-mirrored
  // Dimension applies a local-Y-flip to its measurement text
  // (`applyMirrorScale`'s render convention), which stacks the glyph BELOW
  // its anchor (local Y ∈ [-height, 0]) instead of above (∈ [0, height]).
  // Always building the unmirrored `[0, height]` box let a mirrored
  // label's true footprint extend below the reported WCS bounds, missing
  // it from window selection and spatial-index culling.
  const localMinY = mirrored ? -height : 0;
  const localMaxY = mirrored ? 0 : height;
  const local: Local2[] = [
    { x: -halfWidth, y: localMinY },
    { x: halfWidth, y: localMinY },
    { x: halfWidth, y: localMaxY },
    { x: -halfWidth, y: localMaxY },
  ];
  return local.map((c) => {
    const r = rotateDeg2D(c.x, c.y, rotationDeg);
    return { x: anchor.x + r.x, y: anchor.y + r.y };
  });
}

export function dimensionLocal(e: DimensionEntity, dimStyle?: DimensionStyle): LocalPointSet {
  const pts: Wcs3[] = [
    { x: e.defPoint.x, y: e.defPoint.y, z: 0 },
    { x: e.textMidpoint.x, y: e.textMidpoint.y, z: 0 },
  ];
  if (e.extLine1Point) pts.push({ x: e.extLine1Point.x, y: e.extLine1Point.y, z: 0 });
  if (e.extLine2Point) pts.push({ x: e.extLine2Point.x, y: e.extLine2Point.y, z: 0 });
  if (e.thirdPoint) pts.push({ x: e.thirdPoint.x, y: e.thirdPoint.y, z: e.thirdPoint.z });
  if (e.arcPoint) pts.push({ x: e.arcPoint.x, y: e.arcPoint.y, z: e.arcPoint.z });
  if (e.insertionPoint) pts.push({ x: e.insertionPoint.x, y: e.insertionPoint.y, z: e.insertionPoint.z });

  // @codex T1 arm(c) round-23 P2 finding — the LABEL BOX's own height must resolve DIMTXT the SAME
  // way the renderer actually sizes the drawn glyph, matching `apps/web/…/scene3d/adapters/geom/
  // dimensionGeometry.ts::beginEmit` EXACTLY: `positiveOr(raw.textHeightRaw, style.dimtxt)`, where
  // `raw.textHeightRaw` is `entity.textHeight` verbatim (`DimensionRawFields`'s own doc: "the
  // PRE-CLAMP value") and `style.dimtxt` is the RESOLVED named-DIMSTYLE-table row's own DIMTXT
  // (`dimensionFromEntity.ts`: `dimensionStyleRowToValues(resolveDimensionStyleRow(entity.styleName,
  // ctx.dimensionStyles))` — the SAME table-row `resolveDimensionStyleFromContext` resolves into
  // this function's own `dimStyle` parameter). Per-entity `styleOverrides.dimtxt` deliberately does
  // NOT participate here — `dimensionFromEntity.ts`'s own module doc states this explicitly: DIMVAR
  // resolution for the typed render path "requires resolving DIMVARs from the named DimensionStyle
  // TABLE row ... never from `entity.styleOverrides` (a different, currently render-unwired
  // channel)" — so a DSTYLE XDATA override that has NO effect on the actually-drawn glyph must not
  // silently narrow (under-report what is really drawn) OR widen (claim a size never rendered) the
  // label box either. This resolution is now (@codex round-25 Fix 8) the SAME shape as the `dimtxt`
  // padding variable a few lines below — round-22/round-23 originally described the padding
  // variable as DELIBERATELY DIFFERENT (consulting `styleOverrides.dimtxt` to match
  // `cad-core/leafBounds.ts::dimensionBoundsPoints`'s own SEPARATE, unrelated convention) — that
  // framing is now STALE: round-25 confirmed `styleOverrides` has no effect on either live
  // DIMENSION renderer at all, so the padding variable's `styleOverrides` consultation was dropped
  // too (see `formattedMeasurementText`'s own round-25 doc for the full evidence). The two
  // variables now differ ONLY in which hardcoded hierarchy of fallbacks they use when `dimStyle` is
  // itself absent/malformed (`renderedTextHeight` prefers `e.textHeight` first, matching
  // `beginEmit`'s exact clamp order; the padding `dimtxt` prefers `dimStyle?.DIMTXT` first,
  // matching `cad-core`'s own convention for THAT separate consumer) — not in whether
  // `styleOverrides` participates (it no longer does, for either). The prior code ignored
  // `dimStyle` for the label box entirely (hardcoded `e.textHeight > 0 ? e.textHeight : 2.5`),
  // understating a Dimension whose named DIMSTYLE sets a non-default DIMTXT and carries no
  // per-entity `textHeight` override.
  const renderedTextHeight = positiveOrLocal(e.textHeight, positiveOrLocal(dimStyle?.DIMTXT, 2.5));

  // @codex T1 arm(c) round-22 P2 finding (F2): an empty/"<>" `dimensionText` is NOT "no text" —
  // the renderer (`apps/web/…/scene/dimensionHelpers.ts::formatDimMeasurement`, invoked from
  // `dimensionGeometry.ts::beginEmit`) formats the ACTUAL measurement into a real string
  // (DIMLFAC-scaled, DIMRND-rounded, DIMLUNIT/DIMDEC unit-formatted, subtype-prefixed, DIMPOST-
  // wrapped) BEFORE the text is drawn — using `e.dimensionText.length` directly (0 or 2)
  // drastically UNDER-estimates the width-driven text box for the common blank/auto-measurement
  // case. {@link formattedMeasurementText} is a full, faithful re-derivation of that render-side
  // formula (not a conservative approximation — see its own doc for why a full replication is now
  // proportionate given `dimStyle` is already threaded in by this same round's F4 fix). A REAL
  // non-empty, non-"<>" `dimensionText` is completely UNCHANGED — still `.length` verbatim, exactly
  // as before this fix (the renderer draws that literal string, not a re-formatted measurement,
  // whenever `rawText && rawText !== "<>" && rawText !== ""`).
  const textLengthForWidth =
    e.dimensionText === "" || e.dimensionText === "<>" ? formattedMeasurementText(e, dimStyle).length : e.dimensionText.length;
  // @codex T1 arm(c) round-25 P2 finding (Fix 7): the width-estimate FLOOR term is `2 *
  // renderedTextHeight`, not `1 * renderedTextHeight` — confirmed against the renderer's own
  // `dimensionGeometry.ts::emitMeasurementText` EXACTLY: `halfX = Math.max(content.length *
  // textHeight * 0.3, textHeight)`, i.e. the FULL width is `2 * halfX = Math.max(content.length *
  // textHeight * 0.6, 2 * textHeight)`. The prior floor (`renderedTextHeight`, one height) was
  // HALF the renderer's real floor, under-reporting the box width for any short label whose
  // `content.length * textHeight * 0.6` term falls below `2 * textHeight` (e.g. a 1-2 character
  // measurement at the default 2.5-unit text height).
  // @codex T1 arm(c) round-26 P2 finding (Fix 2): `halfX` matches `emitMeasurementText`'s own
  // `Math.max(content.length * textHeight * 0.3, textHeight)` EXACTLY (the round-25 Fix 7 `width`
  // above is `2 * halfX` — this is that same floor/slope halved, not a re-derivation) — see
  // {@link dimensionMeasurementTextBoxCorners}'s own doc for why the box shape itself changed too.
  const halfX = Math.max(renderedTextHeight * Math.max(textLengthForWidth, 1) * 0.3, renderedTextHeight);
  const box = dimensionMeasurementTextBoxCorners(
    e.textMidpoint,
    halfX,
    renderedTextHeight,
    e.textRotation,
    e.textMirrored ?? false,
  );
  pts.push(...box.map((p) => ({ x: p.x, y: p.y, z: 0 })));

  // @codex T1 arm(c) round-12 P2 finding: DIMEXE (extension-line overshoot)/DIMGAP+DIMTXT (text
  // label clearance) padding around defPoint/extension points, PLUS the ALIGNED-subtype
  // render-projected dim-line points (`alignedDimProjectedPoints`'s doc) — matches
  // `cad-core/leafBounds.ts::dimensionBoundsPoints` EXACTLY (that SEPARATE consumer still has its
  // own documented, unrelated `styleOverrides`-aware convention — see the round-25 doc below for
  // why THIS function no longer shares it). The `dimStyle` tier is clamped via {@link numOrLocal}/
  // {@link positiveOrLocal} — the SAME finite/positive guard `dimensionHelpers.ts
  // ::dimensionStyleRowToValues` applies to the identical fields (DIMEXO/DIMEXE/DIMGAP accept any
  // finite value; DIMTXT — like DIMASZ below — is clamped POSITIVE, matching the render's own
  // `positiveOr(style.DIMTXT, …)`) — replicating the resolution FAITHFULLY, not approximately,
  // means a malformed (0/negative/non-finite) DIMSTYLE-table value must fall through to the
  // hardcoded default here exactly as it does for the render, not silently zero out the pad.
  //
  // @codex T1 arm(c) round-25 P2 finding (Fix 8) — `e.styleOverrides?.field` DROPPED from this
  // precedence chain ENTIRELY (previously `e.styleOverrides?.field ?? dimStyle-or-hardcoded`, giving
  // a per-entity DSTYLE-XDATA override FIRST precedence). Same root cause and same fix as
  // {@link formattedMeasurementText}'s own round-25 doc (read it for the full renderer-tracing
  // evidence): `entity.styleOverrides` has NO EFFECT on what either live DIMENSION renderer
  // (typed scene3d or legacy bag) actually draws for DIMASZ/DIMTXT(padding)/DIMGAP/DIMEXE/DIMEXO —
  // only the resolved named-DIMSTYLE-table row (`dimStyle`) does. Giving `styleOverrides` first
  // precedence could make the reported pad SMALLER than the real, DIMSTYLE-driven rendered
  // footprint whenever a document sets a per-entity override the renderer never applies —
  // an under-report, the one thing this module's whole design exists to prevent. The prior
  // round-12/round-21/round-22/round-23 doc language above ("Per-entity DIMVAR overrides … when
  // present" / "styleOverrides?.field ?? …") described the OLD (now-corrected) precedence; the
  // resolution is now purely `dimStyle?.FIELD ?? <hardcoded AutoCAD default>` for all 5 of these
  // fields, uniformly.
  const dimexe = numOrLocal(dimStyle?.DIMEXE, 1.25);
  const dimexo = numOrLocal(dimStyle?.DIMEXO, 0.625);
  const dimtxt = positiveOrLocal(dimStyle?.DIMTXT, e.textHeight > 0 ? e.textHeight : 2.5);
  const dimgap = numOrLocal(dimStyle?.DIMGAP, 0.625);
  // @codex T1 arm(c) round-21 P2 finding: DIMASZ (arrowhead/tick size) into the SAME conservative
  // pad — `dimensionGeometry.ts::emitArrowOrTick`'s `size: style.dimasz` draws an arrow/tick
  // PRIMITIVE at every dim-line endpoint (`emitArrowPair`/Angular/Radius's own single-arrow calls),
  // and `arrowMarkerMesh.ts`'s unit-size prototypes show a STANDARD triangle/open/dot marker's
  // BASE sits `HALF_WIDTH=0.5` units PERPENDICULAR to the dim line (a real bbox extension this
  // padding-based approach never accounted for before), while the ARCH-TICK/OBLIQUE tick variants
  // extend up to `0.5` unit scaled by `dimasz` in EITHER local axis from the tip
  // (`buildArchTick`/`buildObliqueTick`'s ±0.45/±0.5 unit corners) — including PAST the dim-line
  // endpoint outward, unlike the inward-only triangle body. `dimasz` resolved via the SAME
  // `positiveOrLocal(dimStyle?.field, default)` chain DIMTXT above uses (AutoCAD's documented
  // DIMASZ default, matching `dimensionHelpers.ts::defaultDimStyle.dimasz`; POSITIVE-clamped like
  // DIMTXT — the render's `positiveOr(style.DIMASZ, …)` — not the finite-only `numOrLocal`
  // DIMEXE/DIMEXO/DIMGAP use, since a 0/negative arrow size is schema-valid but the renderer
  // substitutes the default rather than drawing nothing). @codex round-25 Fix 8: `styleOverrides`
  // dropped from this precedence too — see the doc block above.
  const dimasz = positiveOrLocal(dimStyle?.DIMASZ, 2.5);

  // @codex T1 arm(c) round-25 P2 finding (Fix 6) — UNION a second label box anchored at the
  // subtype-specific pose {@link dimensionLabelAnchor} actually computes (see its own doc for the
  // full per-subtype formula derivation), alongside the EXISTING `e.textMidpoint`-anchored `box`
  // above (never replacing it — this module's "never under-report" convention). Same
  // width/height as the master box.
  //
  // @codex T1 arm(c) round-26 P2 finding (Fix 1) — rotation is now
  // {@link dimensionSubtypeTextRotationDeg}, not the master box's unconditional `e.textRotation` —
  // an ALIGNED dimension with `DIMTIH: false` renders its label ROTATED to the extension-line
  // angle (see that helper's own doc); every other subtype's rotation is unchanged (still
  // `e.textRotation`, exactly as before this fix).
  const labelAnchor = dimensionLabelAnchor(e, dimgap, renderedTextHeight);
  const subtypeBox = dimensionMeasurementTextBoxCorners(
    labelAnchor,
    halfX,
    renderedTextHeight,
    dimensionSubtypeTextRotationDeg(e, dimStyle),
    e.textMirrored ?? false,
  );
  pts.push(...subtypeBox.map((p) => ({ x: p.x, y: p.y, z: 0 })));

  for (const p of alignedDimProjectedPoints(e, dimexo, dimexe)) pts.push({ x: p.x, y: p.y, z: 0 });
  const pad = Math.max(Math.abs(dimexe), Math.abs(dimgap) + Math.abs(dimtxt), Math.abs(dimasz));
  if (pad > 0) {
    pts.push({ x: e.defPoint.x + pad, y: e.defPoint.y + pad, z: 0 });
    pts.push({ x: e.defPoint.x - pad, y: e.defPoint.y - pad, z: 0 });
    if (e.extLine1Point) {
      const sign = Math.sign(e.defPoint.y - e.extLine1Point.y) || 1;
      pts.push({ x: e.extLine1Point.x, y: e.extLine1Point.y + sign * pad, z: 0 });
    }
    if (e.extLine2Point) {
      const sign = Math.sign(e.defPoint.y - e.extLine2Point.y) || 1;
      pts.push({ x: e.extLine2Point.x, y: e.extLine2Point.y + sign * pad, z: 0 });
    }
  }

  // @codex T1 arm(c) round-26 P2 finding (Fix 4) — Angular / Angular-3pt dimensions render a whole
  // ARC (`dimensionGeometry.ts::emitAngular`'s `kind: "arc"` primitive) between the two extension
  // rays, not just the raw def/ext/third points — the arc bulges out to `arcRadius` in EVERY
  // direction from its own center `(vx,vy)`, which the extension-ray endpoints alone can badly
  // under-cover whenever both rays stay confined to one quadrant relative to the vertex (a common
  // real-world Angular dimension, e.g. a ~30° angle). `vx`/`vy`/`arcRadius` are the SAME derivation
  // `emitAngular` itself uses (`vx = tx || dx; vy = ty || dy; arcRadius = hypot(dx-vx,dy-vy) ||
  // min(hypot(x1-vx,y1-vy), hypot(x2-vx,y2-vy)) * 0.7`) — {@link dimensionLabelAnchor}'s own
  // Angular/Angular-3pt branch above already re-derives the identical formula for the label anchor,
  // reused here for the arc bound rather than a third re-derivation. This module's established
  // "conservative, not exact arc-span" pattern (matching how Circle/Arc entities themselves are
  // bounded elsewhere — the full center±radius square, not the tighter true sweep): 2 diagonal
  // corners of that square are enough to widen the AABB to `vx±arcRadius`/`vy±arcRadius` in every
  // direction.
  if (dimensionIsAngularSubtype(e.dimensionType)) {
    const x1 = e.extLine1Point?.x ?? 0;
    const y1 = e.extLine1Point?.y ?? 0;
    const x2 = e.extLine2Point?.x ?? 0;
    const y2 = e.extLine2Point?.y ?? 0;
    const dx = e.defPoint.x || e.textMidpoint.x;
    const dy = e.defPoint.y || e.textMidpoint.y;
    const tx = e.thirdPoint?.x ?? 0;
    const ty = e.thirdPoint?.y ?? 0;
    const vx = tx || dx;
    const vy = ty || dy;
    const arcRadius = Math.hypot(dx - vx, dy - vy) || Math.min(Math.hypot(x1 - vx, y1 - vy), Math.hypot(x2 - vx, y2 - vy)) * 0.7;
    pts.push({ x: vx - arcRadius, y: vy - arcRadius, z: 0 });
    pts.push({ x: vx + arcRadius, y: vy + arcRadius, z: 0 });
  }

  const base = withThicknessLocal(pts, e.thickness ?? 0);
  return { points: base, extrusion: e.extrusion };
}

/**
 * A HATCH boundary `arc`/`ellipticArc` edge's `counterclockwise` flag (group 73) decides the
 * actual swept DIRECTION from `startAngle` to `endAngle` (0 = clockwise, 1 = counterclockwise) —
 * unlike the top-level ARC/ELLIPSE entity, whose sweep is ALWAYS CCW start→end by DXF
 * convention. This module's `arcExtentPoints2D`/`tessellateArc2D`/etc. are CCW-only (see
 * `sweepDeg`'s doc). Swapping `startDeg`/`endDeg` before calling them makes a CW sweep(start→end)
 * → CCW sweep(end→start) — the IDENTICAL physical point set, just re-described in the
 * CCW-only convention (@codex T1 arm(c) round-4 P2 finding: a `counterclockwise:false` arc edge
 * was always treated as CCW, reporting only the SHORT complementary arc instead of the true
 * (potentially much larger, e.g. 270°) swept range).
 */
function hatchEdgeAngles(startDeg: number, endDeg: number, counterclockwise: boolean): [number, number] {
  return counterclockwise ? [startDeg, endDeg] : [endDeg, startDeg];
}

/** `dense` — see {@link circleLocal}'s doc (threaded into arc-edge tessellation for a HATCH boundary path). */
export function hatchLocal(e: HatchEntity, dense = false): LocalPointSet {
  const pts: Wcs3[] = [];
  for (const path of e.boundaryPaths) {
    for (const edge of path.edges) {
      switch (edge.edgeType) {
        case "line":
          pts.push({ x: edge.start.x, y: edge.start.y, z: e.elevation }, { x: edge.end.x, y: edge.end.y, z: e.elevation });
          break;
        case "arc": {
          const [a0, a1] = hatchEdgeAngles(edge.startAngle, edge.endAngle, edge.counterclockwise);
          let local2 = arcExtentPoints2D(edge.center.x, edge.center.y, edge.radius, a0, a1);
          if (dense || !isDefaultExtrusion(e.extrusion)) {
            local2 = [...local2, ...tessellateArc2D(edge.center.x, edge.center.y, edge.radius, a0, a1)];
          }
          for (const p of local2) pts.push({ x: p.x, y: p.y, z: e.elevation });
          break;
        }
        case "ellipticArc": {
          const [a0, a1] = hatchEdgeAngles(edge.startAngle, edge.endAngle, edge.counterclockwise);
          let local2 = ellipseExtentPointsDeg2D(edge.center.x, edge.center.y, edge.majorAxisEnd.x, edge.majorAxisEnd.y, edge.minorRatio, a0, a1);
          if (dense || !isDefaultExtrusion(e.extrusion)) {
            local2 = [
              ...local2,
              ...tessellateEllipseDeg2D(edge.center.x, edge.center.y, edge.majorAxisEnd.x, edge.majorAxisEnd.y, edge.minorRatio, a0, a1),
            ];
          }
          for (const p of local2) pts.push({ x: p.x, y: p.y, z: e.elevation });
          break;
        }
        default: {
          const exhaustive: never = edge;
          void exhaustive;
        }
      }
    }
  }
  return { points: pts, extrusion: e.extrusion };
}

export function viewportLocal(e: ViewportEntity): LocalPointSet {
  const hw = e.width / 2;
  const hh = e.height / 2;
  return {
    points: [
      { x: e.center.x - hw, y: e.center.y - hh, z: 0 },
      { x: e.center.x + hw, y: e.center.y - hh, z: 0 },
      { x: e.center.x + hw, y: e.center.y + hh, z: 0 },
      { x: e.center.x - hw, y: e.center.y + hh, z: 0 },
    ],
    extrusion: undefined,
  };
}

/**
 * `ArrayRectEntity.axisAngle` unit discriminant — mirrors `cad-core/common.ts`'s
 * `isArrayAxisAngleRadians` (private helper backing `rotateByArrayAxisAngle`/
 * `arrayAxisAngleDeg`) EXACTLY. `drawing-model` cannot import `cad-core` (dependency
 * direction — `cad-core` imports `drawing-model`, never the reverse, see this module's
 * header and `wcsBounds.ts`'s), so the identical three-way split is RE-DERIVED here
 * rather than shared. @codex T1 arm(c) round-24 P2 finding: the prior code below gated
 * the unit purely on `e.associative` — treating EVERY associative array's `axisAngle`
 * as RADIANS. But the DOMINANT associative case (`axisAngleFromNamedForm: true`, the
 * graph's named "AxesAngle" value-param resolved) is ALREADY in DEGREES — AW-1's units
 * seam (`dxf-io/handleResolution.ts::axesAngleRadiansToModelDegrees`) converts AutoCAD's
 * on-wire radians to the model's degrees convention at IMPORT time. Re-converting that
 * already-degrees value as if it were radians turned a 90°-intended array into a
 * ~5157° (90 · 180/π) rotation. Only the ONE residual case — associative AND a value
 * resolved from the standalone positional `ACDBASSOCARRAYRECTANGULARPARAMETERS` record
 * (`assocParamsSnapshot?.axisAngle !== undefined`) BUT NOT from the named form — is
 * genuinely radians (AW-1 deliberately never unit-converts that form, no fixture proves
 * its wire convention). Every other case (associative but neither form resolved, or a
 * bare non-associative block-ref grid) is degrees, group-50 always is. See
 * `cad-core/common.ts::rotateByArrayAxisAngle`'s doc for the full case-split rationale
 * this mirrors. This discriminant only picks the UNIT; {@link arrayRectAxes}'s own round-26 doc
 * covers the separate NEUTRAL-BASELINE (90°/π/2) correction layered on top, once the unit is known.
 */
function isArrayAxisAngleRadians(e: {
  associative: boolean;
  assocParamsSnapshot?: { axisAngle?: number };
  axisAngleFromNamedForm?: boolean;
}): boolean {
  return e.associative && e.assocParamsSnapshot?.axisAngle !== undefined && !e.axisAngleFromNamedForm;
}

/**
 * The grid's row/column unit axis vectors — real `xAxisDirection`/`yAxisDirection` when present,
 * else derived from `axisAngle`.
 *
 * @codex T1 arm(c) round-26 P1 finding: NEUTRAL-BASELINE correction, mirroring
 * `cad-core/common.ts::rotateByArrayAxisAngle`'s round-9 fix (`fix(array-spec-render-arm):
 * AxesAngle neutral baseline is pi/2, not 0`, #536 — merged into `main` AFTER round-24 first
 * introduced the unit discriminant above, so `arrayRectAxes` never picked it up). Per the resolved
 * `AcDbAssocArrayRectangularParameters` "AxesAngle" ODA semantics that doc's `rotateByArrayAxisAngle`
 * cites (`CalculateDefaultMatrix`: `Y-axis = X-axis-direction.rotateBy(AxesAngle)`), `axisAngle` is
 * the angle BETWEEN the row and column axes, not the array's absolute visual rotation — a STANDARD
 * orthogonal associative array (rows ⟂ columns, the overwhelmingly common case) persists
 * `AxesAngle = π/2` (90°) even though it is VISUALLY UNROTATED (columns along +X, rows along +Y,
 * the same layout a non-associative array's 0° neutral produces). Before this fix, `arrayRectAxes`
 * rotated the unit column/row vectors by the RAW angle (after the round-24 unit conversion, but with
 * no baseline subtracted) — so a standard orthogonal associative ARRAYRECT's reported bounds axes
 * came out rotated 90° relative to what the renderer, grips, and selection (cad-core's three
 * `rotateByArrayAxisAngle` consumers, all fixed by #536) actually draw/pick, for exactly the
 * overwhelmingly common case. This subtracts the SAME 90°/π/2 baseline for every `associative: true`
 * entity, in WHICHEVER unit {@link isArrayAxisAngleRadians} says the value is in — matching
 * `rotateByArrayAxisAngle`'s `e.associative ? angleDeg - 90 : angleDeg` (degrees branch) /
 * `angleRad - Math.PI / 2` (radians branch) exactly.
 *
 * Equivalence proof this port relies on: `arrayRectAxes` converts radians→degrees FIRST (the line
 * above `correctedDeg`), then applies the baseline subtraction entirely in the degrees domain —
 * `rotateByArrayAxisAngle` instead subtracts the baseline BEFORE any unit conversion, in whichever
 * unit the raw value already carries. These give bit-for-bit identical angles: for the radians
 * branch, `rotateByArrayAxisAngle` computes `rotateRad(x, y, r - π/2)` for raw radians `r`; this
 * function computes `angleDeg = r·180/π`, then `angleDeg - 90 = r·180/π - 90 = (r - π/2)·180/π` —
 * the same angle re-expressed in degrees, so `rotateDeg2D(x, y, angleDeg - 90)` and
 * `rotateRad(x, y, r - π/2)` rotate by the identical angle. `rotateDeg2D` (`wcsBoundsMath.ts`) and
 * `rotateDeg`/`rotateRad` (`cad-core/common.ts`) use the IDENTICAL CCW/DXF-convention formula
 * (`x·cos(a) - y·sin(a)`, `x·sin(a) + y·cos(a)`), confirmed by reading both before this fix, so the
 * shared sign convention carries through and the subtraction doesn't flip rotation direction.
 */
function arrayRectAxes(e: {
  axisAngle: number;
  associative: boolean;
  xAxisDirection?: { x: number; y: number; z: number };
  yAxisDirection?: { x: number; y: number; z: number };
  assocParamsSnapshot?: { axisAngle?: number };
  axisAngleFromNamedForm?: boolean;
}): { colAxis: Wcs3; rowAxis: Wcs3 } {
  if (e.xAxisDirection && e.yAxisDirection) {
    return { colAxis: e.xAxisDirection, rowAxis: e.yAxisDirection };
  }
  const angleDeg = isArrayAxisAngleRadians(e) ? (e.axisAngle * 180) / Math.PI : e.axisAngle;
  const correctedDeg = e.associative ? angleDeg - 90 : angleDeg;
  const col2 = rotateDeg2D(1, 0, correctedDeg);
  const row2 = rotateDeg2D(0, 1, correctedDeg);
  return { colAxis: { x: col2.x, y: col2.y, z: 0 }, rowAxis: { x: row2.x, y: row2.y, z: 0 } };
}

/**
 * The grid's column/row/level SPAN — derived from `count`/`spacing` (`(count-1)·spacing`),
 * matching `cad-core`'s established `arrayRectBounds` (`leafBounds.ts`), `arrayRectGrips`
 * (`grips.ts`), and `selectLeaf.ts`'s IDENTICAL derivation — @codex T1 arm(c) round-9 P2
 * finding: the prior code read `e.columnTotal`/`e.rowTotal`/`e.levelTotal` directly, but those
 * fields are a REDUNDANT, DERIVED cache the schema defaults to 100 (`columnTotal: z.number()
 * .default(100)`) and at least one dxfBridge producer populates with a DIFFERENT (also-wrong,
 * `count·spacing` not `(count-1)·spacing`) formula — so after an edited `columnCount`/
 * `columnSpacing` with a stale/never-refreshed `columnTotal`, the prior code under/over-reported
 * the grid's true rendered/selectable extent. `levelCount`/`levelSpacing` have no cad-core 2D
 * `arrayRectBounds` precedent (that function is 2D-only, X/Y — this 3D bounds helper is the
 * first consumer of the Z-stack fields), so the level span uses the SAME `(count-1)·spacing`
 * pattern for consistency; absent `levelCount`/`levelSpacing` ⇒ span 0 (a single, un-stacked level).
 */
function arrayRectSpans(e: ArrayRectEntity): { colTotal: number; rowTotal: number; levelTotal: number; rowElevationTotal: number } {
  const cols = Math.max(1, Math.round(e.columnCount));
  const rows = Math.max(1, Math.round(e.rowCount));
  const levels = e.levelCount !== undefined ? Math.max(1, Math.round(e.levelCount)) : 1;
  return {
    colTotal: (cols - 1) * e.columnSpacing,
    rowTotal: (rows - 1) * e.rowSpacing,
    levelTotal: (levels - 1) * (e.levelSpacing ?? 0),
    // `rowElevation` is documented ("Per-row Z elevation increment (3D rectangular array)") as a
    // PER-ROW step, matching AutoCAD's ARRAYRECT row-elevation behavior (a "staircase" Z profile
    // — row i sits at Z = i·rowElevation) — @codex T1 arm(c) round-10 P2 finding: the prior code
    // applied `rowElevation * rowFrac` (rowFrac ∈ {0,1}), capping the total Z contribution at a
    // flat `rowElevation` regardless of `rowCount`, instead of the full `(rowCount-1)·rowElevation`
    // span a multi-row array actually spans. No other consumer in this codebase applies
    // `rowElevation` geometrically yet (round-trip-only elsewhere) — this bounds helper is the
    // first, so the `(count-1)·increment` convention already established for col/row/level here
    // is the only precedent to match.
    rowElevationTotal: (rows - 1) * (e.rowElevation ?? 0),
  };
}

export function arrayRectLocal(e: ArrayRectEntity): LocalPointSet {
  const base = e.basePoint;
  const { colAxis, rowAxis } = arrayRectAxes(e);
  const normal = e.basePlaneNormal ?? { x: 0, y: 0, z: 1 };
  const bz = Z(base);
  const { colTotal, rowTotal, levelTotal, rowElevationTotal } = arrayRectSpans(e);
  const corners: Wcs3[] = [];
  for (const colFrac of [0, 1]) {
    for (const rowFrac of [0, 1]) {
      for (const levelFrac of [0, 1]) {
        corners.push({
          x: base.x + colAxis.x * colTotal * colFrac + rowAxis.x * rowTotal * rowFrac + normal.x * levelTotal * levelFrac,
          y: base.y + colAxis.y * colTotal * colFrac + rowAxis.y * rowTotal * rowFrac + normal.y * levelTotal * levelFrac,
          z:
            bz +
            colAxis.z * colTotal * colFrac +
            rowAxis.z * rowTotal * rowFrac +
            normal.z * levelTotal * levelFrac +
            rowElevationTotal * rowFrac,
        });
      }
    }
  }
  return { points: corners, extrusion: undefined };
}
